/* eslint-disable no-template-curly-in-string */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import JoditEditor from 'jodit-react';
import 'jodit/es2015/jodit.min.css';

import { generateEmailHtml } from './GenerateSection';
import { getFontStack, DEFAULT_FONT_FAMILY } from '../../utils/fonts';
import { ensureAiConfig } from '../../utils/aiConfig';
import { ResponsiveDeviceToggle } from '../common';
import { DEVICE_PRESETS, ensureDeviceKey } from '../../utils/devicePresets';
import { apiUrl } from '../../api/client';
import { getAuthHeaders } from '../../api/api';
import { sanitizeEmailHtml } from '../../utils/sanitizeHtml';
import {
  wrapScriptletsWithPlaceholders,
  unwrapScriptletPlaceholders,
} from '../../utils/scriptletPlaceholders';

const WORKSPACE_CLASS = 'kit-visual-editor-workspace';
const AUTO_SAVE_DELAY = 800;
const DYNAMIC_THEME_STYLE_ID = 'kit-visual-editor-theme';
const VISUAL_EDITOR_AI_ENABLED = false;

const formatRelativeTime = (timestamp, relativeTo = Date.now()) => {
  if (!timestamp) return '';
  const diff = Math.max(0, relativeTo - timestamp);
  if (diff < 5000) return 'just now';
  if (diff < 60000) return `${Math.round(diff / 1000)}s ago`;
  if (diff < 3600000) return `${Math.round(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.round(diff / 3600000)}h ago`;
  const days = Math.round(diff / 86400000);
  return `${days}d ago`;
};

const stripLeadingStyleTag = html => {
  if (!html || typeof html !== 'string') return '';
  return html.replace(/^\s*<style[\s\S]*?<\/style>/i, '').trim();
};

const extractBody = html => {
  if (!html || typeof html !== 'string') return '';
  const protectedHtml = wrapScriptletsWithPlaceholders(html);
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(protectedHtml, 'text/html');
    const inner = doc.body?.innerHTML?.trim() || protectedHtml.trim();
    return unwrapScriptletPlaceholders(inner);
  } catch (error) {
    return unwrapScriptletPlaceholders(protectedHtml).trim();
  }
};

const hasTableLayout = html =>
  typeof html === 'string' && /<(table|tr|td|tbody|thead|tfoot)/i.test(html);

const isPlainVisualHtml = (html, settings) => {
  if (!html || typeof html !== 'string') return true;
  const trimmed = html.trim();
  if (!trimmed) return true;
  if (!/<[a-z][^>]*>/i.test(trimmed)) return true;
  if (settings?.regmailEnabled && !hasTableLayout(trimmed)) return true;
  const textOnly = trimmed.replace(/<[^>]+>/g, '').trim();
  if (!textOnly) return false;
  const ratio = textOnly.length / Math.max(trimmed.length, 1);
  return ratio > 0.85;
};

const sanitizeBlockHtml = html => {
  if (typeof html !== 'string') return html || '';
  return html
    .trim()
    .replace(/>\s+</g, '><')
    .replace(/>\s+/g, '>');
};

const buildBlockLibrary = ({
  fontFamily,
  fontSize,
  lineHeight,
  textColor,
  dividerColor,
}) => {
  const blocks = [
    {
      id: 'text-block',
      label: 'Sample paragraph',
      description: 'Drop in a placeholder paragraph you can immediately replace.',
      html: `
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="width: 100%; margin-bottom: 16px;">
          <tbody>
            <tr>
              <td>
                <p style="font-size: ${fontSize}; line-height: ${lineHeight}; margin: 0; color: ${textColor}; font-family: ${fontFamily};">
                  This is a sample paragraph. Please replace this with your email content.
                </p>
              </td>
            </tr>
          </tbody>
        </table>
      `,
    },
    {
      id: 'two-columns',
      label: 'Two columns',
      description: '50/50 column layout for comparisons or highlights.',
      html: `
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="width:100%;max-width:600px;margin:0 auto;background:#ffffff;">
          <tbody>
            <tr>
              <td style="width:50%;padding:0;font-family:${fontFamily};font-size:${fontSize};line-height:${lineHeight};color:${textColor};text-align:left;">
                <h3 style="margin:0 0 10px;font-weight:600;">Left column</h3>
                <p style="margin:0;">Use columns to compare benefits or share supporting details.</p>
              </td>
              <td style="width:50%;padding:0;font-family:${fontFamily};font-size:${fontSize};line-height:${lineHeight};color:${textColor};text-align:left;">
                <h3 style="margin:0 0 10px;font-weight:600;">Right column</h3>
                <p style="margin:0;">Keep copy balanced so the layout looks intentional.</p>
              </td>
            </tr>
          </tbody>
        </table>
      `,
    },
    {
      id: 'three-columns',
      label: 'Three columns',
      description: 'Three equal columns for feature lists or stats.',
      html: `
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="width:100%;max-width:600px;margin:0 auto;background:#ffffff;">
          <tbody>
            <tr>
              ${[1, 2, 3]
                .map(
                  index => `
                    <td style="width:33.333%;padding:0;font-family:${fontFamily};font-size:${fontSize};line-height:${lineHeight};color:${textColor};text-align:left;">
                      <h3 style="margin:0 0 8px;font-weight:600;">Feature ${index}</h3>
                      <p style="margin:0;">Short supporting copy goes here.</p>
                    </td>
                  `
                )
                .join('')}
            </tr>
          </tbody>
        </table>
      `,
    },
    {
      id: 'divider',
      label: 'Divider',
      description: 'Horizontal divider to separate sections.',
      html: `
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="width:100%;max-width:600px;margin:24px auto;" data-divider-block="true">
          <tbody>
            <tr>
              <td data-divider-line="true" style="height:1px;background:${dividerColor};font-size:0;line-height:0;">&nbsp;</td>
            </tr>
          </tbody>
        </table>
      `,
    },
    {
      id: 'spacer',
      label: 'Spacer',
      description: 'Add vertical breathing room between sections.',
      html: `
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="width:100%;max-width:600px;margin:0 auto;">
          <tbody>
            <tr><td style="height:32px;font-size:0;line-height:0;">&nbsp;</td></tr>
          </tbody>
        </table>
      `,
    },
  ];

  return blocks.map(block => ({
    ...block,
    html: sanitizeBlockHtml(block.html),
  }));
};

const ensureWorkspaceStyles = () => {
  if (document.getElementById('kit-visual-editor-inline')) return;
  const style = document.createElement('style');
  style.id = 'kit-visual-editor-inline';
  style.innerHTML = `
    .${WORKSPACE_CLASS} { display:flex; flex-direction:column; gap:20px; }
    .${WORKSPACE_CLASS} .kit-editor-panels { display:grid; gap:16px; grid-template-columns:minmax(0,1fr); }
    @media (min-width:1280px) {
      .${WORKSPACE_CLASS} .kit-editor-panels { grid-template-columns:240px minmax(0,1fr); }
    }
    .${WORKSPACE_CLASS}.kit-fullscreen-active {
      position:fixed; inset:0; background:#f1f5f9; padding:24px; z-index:10000; display:flex; flex-direction:column; gap:16px;
    }
    .${WORKSPACE_CLASS}.kit-fullscreen-active .kit-editor-panels {
      grid-template-columns:280px minmax(0,1fr); height:100%;
    }
    .${WORKSPACE_CLASS} .kit-block-card {
      border:1px solid rgba(148,163,184,0.35); border-radius:14px; background:#fff; padding:12px 14px; cursor:pointer; display:flex; flex-direction:column; gap:6px; transition:border-color .2s, box-shadow .2s;
    }
    .${WORKSPACE_CLASS} .kit-block-card:hover { border-color:#2563eb; box-shadow:0 10px 18px rgba(37,99,235,0.12); }
    .${WORKSPACE_CLASS} .kit-block-card h4 { margin:0; font-size:13pt; font-weight:600; color:#0f172a; }
    .${WORKSPACE_CLASS} .kit-block-card p { margin:0; font-size:12pt; color:#475569; line-height:1.4; }
    .${WORKSPACE_CLASS} .kit-rich-editor .kit-custom-toolbar-container {
      position:sticky;
      top:0;
      z-index:30;
      margin-bottom:16px;
      padding:8px 0 12px;
      background:linear-gradient(180deg, rgba(248,250,252,0.97) 0%, rgba(248,250,252,0.82) 65%, rgba(248,250,252,0) 100%);
    }
    .${WORKSPACE_CLASS} .kit-custom-toolbar {
      display:flex;
      flex-wrap:wrap;
      gap:8px 12px;
      border:1px solid rgba(148,163,184,0.35);
      border-radius:16px;
      padding:10px 12px;
      background:rgba(255,255,255,0.95);
      box-shadow:0 8px 22px rgba(15,23,42,0.08);
    }
    .${WORKSPACE_CLASS} .kit-toolbar-group {
      display:flex;
      align-items:center;
      gap:6px;
      flex-wrap:wrap;
    }
    .${WORKSPACE_CLASS} .kit-fontsize-control {
      display:flex;
      align-items:center;
      gap:6px;
      border:1px solid rgba(148,163,184,0.35);
      border-radius:10px;
      padding:4px 8px;
      background:#f8fafc;
    }
    .${WORKSPACE_CLASS} .kit-fontsize-control label {
      font-size:11pt;
      font-weight:600;
      color:#475569;
    }
    .${WORKSPACE_CLASS} .kit-fontsize-control input {
      width:56px;
      border:none;
      background:transparent;
      font-size:12pt;
      font-weight:600;
      color:#0f172a;
      padding:4px 2px;
      text-align:right;
    }
    .${WORKSPACE_CLASS} .kit-fontsize-control input:focus {
      outline:2px solid rgba(37,99,235,0.35);
      border-radius:6px;
      background:#ffffff;
    }
    .${WORKSPACE_CLASS} .kit-toolbar-divider {
      width:1px;
      align-self:stretch;
      background:rgba(148,163,184,0.35);
      margin:0 4px;
    }
    .${WORKSPACE_CLASS} .kit-toolbar-button {
      display:inline-flex;
      align-items:center;
      justify-content:center;
      gap:4px;
      font-size:12pt;
      font-weight:600;
      padding:6px 8px;
      border-radius:10px;
      border:1px solid rgba(148,163,184,0.4);
      color:#0f172a;
      background:rgba(226,232,240,0.45);
      cursor:pointer;
      transition:background .2s, border-color .2s, color .2s, box-shadow .2s;
    }
    .${WORKSPACE_CLASS} .kit-toolbar-button:hover:not(.kit-toolbar-button--disabled) {
      border-color:rgba(37,99,235,0.55);
      color:#1e40af;
    }
    .${WORKSPACE_CLASS} .kit-toolbar-button--active {
      border-color:rgba(37,99,235,0.7);
      background:rgba(59,130,246,0.16);
      color:#1d4ed8;
      box-shadow:0 0 0 1px rgba(59,130,246,0.18);
    }
    .${WORKSPACE_CLASS} .kit-toolbar-button--disabled {
      opacity:0.55;
      cursor:not-allowed;
    }
    .${WORKSPACE_CLASS} .kit-toolbar-button--compact {
      padding:5px 7px;
      font-size:11pt;
    }
    .${WORKSPACE_CLASS} .kit-toolbar-button__glyph {
      width:16px;
      height:16px;
      border-radius:8px;
      background:#0f172a;
      color:#ffffff;
      display:flex;
      align-items:center;
      justify-content:center;
      font-size:11pt;
      font-weight:700;
      letter-spacing:0.5px;
    }
    .${WORKSPACE_CLASS} .kit-toolbar-button__icon {
      width:18px;
      height:18px;
      color:inherit;
      display:flex;
      align-items:center;
      justify-content:center;
    }
    .${WORKSPACE_CLASS} .kit-toolbar-select {
      display:inline-flex;
      align-items:center;
      border:1px solid rgba(148,163,184,0.35);
      border-radius:10px;
      padding:0 10px;
      background:#f8fafc;
    }
    .${WORKSPACE_CLASS} .kit-toolbar-select select {
      border:none;
      background:transparent;
      font-size:12pt;
      font-weight:600;
      color:#0f172a;
      padding:6px 0;
      outline:none;
    }
    .${WORKSPACE_CLASS} .kit-toolbar-select select:focus {
      outline:none;
    }
    .${WORKSPACE_CLASS} .kit-link-popover {
      position:absolute;
      min-width:260px;
      max-width:320px;
      z-index:20;
      background:#ffffff;
      border-radius:16px;
      border:1px solid rgba(148,163,184,0.35);
      box-shadow:0 24px 44px rgba(15,23,42,0.18);
      padding:16px;
    }
    .${WORKSPACE_CLASS} .kit-link-type-switch {
      display:flex;
      gap:6px;
      margin-bottom:12px;
    }
    .${WORKSPACE_CLASS} .kit-link-type-switch button {
      flex:1;
      font-size:11pt;
      font-weight:600;
      padding:6px 8px;
      border-radius:9px;
      border:1px solid rgba(148,163,184,0.35);
      background:#f8fafc;
      color:#475569;
      cursor:pointer;
      transition:background .2s, border-color .2s, color .2s;
    }
    .${WORKSPACE_CLASS} .kit-link-type-switch button.kit-link-type-switch__active {
      border-color:rgba(37,99,235,0.6);
      background:rgba(37,99,235,0.15);
      color:#1d4ed8;
    }
    .${WORKSPACE_CLASS} .kit-link-popover label {
      display:block;
      font-size:11pt;
      font-weight:600;
      color:#475569;
      margin-bottom:4px;
    }
    .${WORKSPACE_CLASS} .kit-link-popover input {
      width:100%;
      border:1px solid rgba(148,163,184,0.35);
      border-radius:10px;
      padding:8px 10px;
      font-size:12pt;
      background:#f8fafc;
      margin-bottom:10px;
    }
    .${WORKSPACE_CLASS} .kit-link-popover input:focus {
      outline:2px solid rgba(37,99,235,0.35);
      outline-offset:1px;
      background:#ffffff;
    }
    .${WORKSPACE_CLASS} .kit-link-popover__helper {
      font-size:11pt;
      color:#64748b;
      margin-bottom:12px;
    }
    .${WORKSPACE_CLASS} .kit-link-popover__error {
      color:#dc2626;
      font-size:11pt;
      font-weight:600;
      margin-bottom:8px;
    }
    .${WORKSPACE_CLASS} .kit-link-popover__actions {
      display:flex;
      justify-content:flex-end;
      gap:8px;
    }
    .${WORKSPACE_CLASS} .kit-link-popover__actions button {
      font-size:12pt;
      font-weight:600;
      padding:7px 12px;
      border-radius:9px;
      border:1px solid rgba(148,163,184,0.35);
      background:#f8fafc;
      cursor:pointer;
    }
    .${WORKSPACE_CLASS} .kit-link-popover__actions button.kit-primary {
      background:#2563eb;
      border-color:#2563eb;
      color:#ffffff;
    }
    .${WORKSPACE_CLASS} .kit-link-popover__actions button:hover {
      border-color:rgba(37,99,235,0.6);
    }
    .${WORKSPACE_CLASS} .kit-view-toggle {
      display:inline-flex;
      gap:4px;
      padding:4px;
      border-radius:9999px;
      border:1px solid rgba(148,163,184,0.35);
      background:rgba(248,250,252,0.9);
    }
    .${WORKSPACE_CLASS} .kit-view-toggle button {
      font-size:11pt;
      font-weight:600;
      padding:6px 12px;
      border-radius:9999px;
      border:1px solid transparent;
      background:transparent;
      color:#475569;
      cursor:pointer;
      transition:background .2s, color .2s, border-color .2s, box-shadow .2s;
    }
    .${WORKSPACE_CLASS} .kit-view-toggle button:hover {
      color:#1d4ed8;
    }
    .${WORKSPACE_CLASS} .kit-view-toggle button.kit-view-toggle__active {
      background:#ffffff;
      color:#1d4ed8;
      border-color:rgba(37,99,235,0.6);
      box-shadow:0 6px 15px rgba(15,23,42,0.12);
    }
    .${WORKSPACE_CLASS} .kit-preview-canvas {
      border:1px solid rgba(148,163,184,0.4);
      border-radius:18px;
      background:#ffffff;
      min-height:620px;
      overflow:auto;
    }
    .${WORKSPACE_CLASS} .kit-preview-canvas--mobile {
      border:1px solid rgba(148,163,184,0.4);
      background:linear-gradient(135deg, #eef2ff, #fdf2f8);
      border-radius:32px;
      padding:16px;
      box-shadow:0 20px 45px rgba(15,23,42,0.18);
      overflow:hidden;
      min-height:auto;
    }
    .${WORKSPACE_CLASS} .kit-preview-canvas iframe {
      display:block;
    }
    .${WORKSPACE_CLASS} .kit-preview-canvas--mobile iframe {
      border-radius:20px;
      background:#ffffff;
      box-shadow:inset 0 0 0 1px rgba(15,23,42,0.08);
    }
    .${WORKSPACE_CLASS} .kit-rich-editor .jodit-container {
      border:1px solid rgba(148,163,184,0.4);
      border-radius:18px;
      background:#ffffff;
      min-height:620px;
      overflow:hidden;
      box-shadow:none;
    }
    .${WORKSPACE_CLASS} .kit-rich-editor .jodit-wysiwyg {
      min-height:620px;
      padding:32px 36px;
      background:transparent;
    }
    .${WORKSPACE_CLASS} .kit-rich-editor .jodit-placeholder {
      padding:32px 36px;
      color:rgba(71,85,105,0.65);
    }
    .${WORKSPACE_CLASS} .kit-rich-editor .jodit-status-bar {
      border-radius:12px;
      border:1px solid rgba(148,163,184,0.2);
      background:rgba(248,250,252,0.85);
      margin-top:12px;
    }
  `;
  document.head.appendChild(style);
};

const LINK_TYPES = [
  { id: 'web', label: 'Web link' },
  { id: 'email', label: 'Email' },
  { id: 'phone', label: 'Phone' },
  { id: 'location', label: 'Location' },
];

const LINK_TYPE_COPY = {
  web: {
    label: 'Web link',
    addressLabel: 'URL',
    placeholder: 'https://example.com',
    helper: 'Opens in a new browser tab.',
  },
  email: {
    label: 'Email',
    addressLabel: 'Email address',
    placeholder: 'team@example.com',
    helper: "Opens the reader's mail client.",
  },
  phone: {
    label: 'Phone',
    addressLabel: 'Phone number',
    placeholder: '+1 234 567 890',
    helper: 'Taps launch the dialer on supported devices.',
  },
  location: {
    label: 'Location',
    addressLabel: 'Location or address',
    placeholder: '123 Main St, Berlin',
    helper: 'Opens Google Maps with this location.',
  },
};

const SUPPORTED_BLOCK_TAGS = ['p', 'h1', 'h2', 'h3', 'h4', 'blockquote'];

const FONT_SIZE_PRESETS = [12, 14, 16, 18, 20, 24, 28, 32, 36, 42, 48];
const FONT_SIZE_INPUT_ID = 'kit-editor-font-size';
const FONT_SIZE_OPTIONS_ID = 'kit-editor-font-size-options';

const normalizePhoneValue = input => {
  const safe = (input || '').trim();
  if (!safe) return '';
  const hasLeadingPlus = safe.startsWith('+');
  const digits = safe.replace(/[^0-9]/g, '');
  return hasLeadingPlus ? `+${digits}` : digits;
};

const buildHrefForLinkType = (type, value) => {
  const trimmed = (value || '').trim();
  if (!trimmed) return '';
  switch (type) {
    case 'email':
      return `mailto:${trimmed}`;
    case 'phone': {
      const normalized = normalizePhoneValue(trimmed);
      return normalized ? `tel:${normalized}` : '';
    }
    case 'location':
      return `https://maps.google.com/?q=${encodeURIComponent(trimmed)}`;
    case 'web':
    default: {
      if (/^[a-z]+:\/\//i.test(trimmed)) {
        return trimmed;
      }
      if (/^mailto:|^tel:/i.test(trimmed)) {
        return trimmed;
      }
      return `https://${trimmed.replace(/^\/+/, '')}`;
    }
  }
};

const isGoogleMapsHref = href => {
  if (!href || typeof href !== 'string') return false;
  const normalized = href.trim();
  if (!normalized) return false;
  try {
    const url = new URL(normalized, normalized.startsWith('http') ? undefined : 'https://maps.google.com');
    const host = url.hostname.toLowerCase();
    return host.includes('google') && url.pathname.toLowerCase().includes('maps');
  } catch (error) {
    return /^https?:\/\/(maps\.)?google\./i.test(normalized);
  }
};

const extractLocationValue = href => {
  if (!href) return '';
  try {
    const url = new URL(href, href.startsWith('http') ? undefined : 'https://maps.google.com');
    const query = url.searchParams.get('q');
    if (query) return decodeURIComponent(query);
    const path = url.pathname.replace(/^\/+/u, '');
    if (path) return decodeURIComponent(path.replace(/\+/g, ' '));
  } catch (error) {
    /* noop */
  }
  return href;
};

const parseHrefForLink = href => {
  if (!href || typeof href !== 'string') {
    return { type: 'web', value: '' };
  }
  if (/^mailto:/i.test(href)) {
    return { type: 'email', value: href.replace(/^mailto:/i, '') };
  }
  if (/^tel:/i.test(href)) {
    return { type: 'phone', value: href.replace(/^tel:/i, '') };
  }
  if (isGoogleMapsHref(href)) {
    return { type: 'location', value: extractLocationValue(href) };
  }
  return { type: 'web', value: href.replace(/^https?:\/\//i, '') };
};

const findAncestorElement = (node, predicate, boundary) => {
  let current = node;
  while (current && current !== boundary) {
    if (current.nodeType === 1 && predicate(current)) {
      return current;
    }
    current = current.parentNode;
  }
  return null;
};

const iconCommonProps = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const IconAlignLeft = props => (
  <svg {...iconCommonProps} {...props}>
    <line x1="4" y1="6" x2="20" y2="6" />
    <line x1="4" y1="12" x2="14" y2="12" />
    <line x1="4" y1="18" x2="20" y2="18" />
  </svg>
);

const IconAlignCenter = props => (
  <svg {...iconCommonProps} {...props}>
    <line x1="6" y1="6" x2="18" y2="6" />
    <line x1="4" y1="12" x2="20" y2="12" />
    <line x1="6" y1="18" x2="18" y2="18" />
  </svg>
);

const IconAlignRight = props => (
  <svg {...iconCommonProps} {...props}>
    <line x1="4" y1="6" x2="20" y2="6" />
    <line x1="10" y1="12" x2="20" y2="12" />
    <line x1="4" y1="18" x2="20" y2="18" />
  </svg>
);

const IconAlignJustify = props => (
  <svg {...iconCommonProps} {...props}>
    <line x1="4" y1="6" x2="20" y2="6" />
    <line x1="4" y1="12" x2="20" y2="12" />
    <line x1="4" y1="18" x2="20" y2="18" />
  </svg>
);

const IconListBulleted = props => (
  <svg {...iconCommonProps} {...props}>
    <circle cx="6" cy="7" r="1.5" fill="currentColor" />
    <circle cx="6" cy="12" r="1.5" fill="currentColor" />
    <circle cx="6" cy="17" r="1.5" fill="currentColor" />
    <line x1="10" y1="7" x2="20" y2="7" />
    <line x1="10" y1="12" x2="20" y2="12" />
    <line x1="10" y1="17" x2="20" y2="17" />
  </svg>
);

const IconListNumbered = props => (
  <svg {...iconCommonProps} {...props}>
    <text
      x="6"
      y="8"
      fontFamily="Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
      fontSize="6"
      fontWeight="600"
      textAnchor="middle"
      fill="currentColor"
    >
      1
    </text>
    <text
      x="6"
      y="13"
      fontFamily="Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
      fontSize="6"
      fontWeight="600"
      textAnchor="middle"
      fill="currentColor"
    >
      2
    </text>
    <text
      x="6"
      y="18"
      fontFamily="Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
      fontSize="6"
      fontWeight="600"
      textAnchor="middle"
      fill="currentColor"
    >
      3
    </text>
    <line x1="10" y1="7" x2="20" y2="7" />
    <line x1="10" y1="12" x2="20" y2="12" />
    <line x1="10" y1="17" x2="20" y2="17" />
  </svg>
);

const IconChain = props => (
  <svg {...iconCommonProps} {...props}>
    <path d="M10.5 7.5 9.4 8.6a3 3 0 0 0 0 4.2 3 3 0 0 0 4.2 0l1.1-1.1" />
    <path d="M13.5 16.5 14.6 15.4a3 3 0 0 0 0-4.2 3 3 0 0 0-4.2 0L9.3 12" />
  </svg>
);

const IconUnlink = props => (
  <svg {...iconCommonProps} {...props}>
    <path d="M9.5 7.5 8.4 8.6a3 3 0 0 0 0 4.2L9.6 14" />
    <path d="M14.5 16.5 15.6 15.4a3 3 0 0 0 0-4.2L14.4 10" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const IconEraser = props => (
  <svg {...iconCommonProps} {...props}>
    <path d="M4.5 14.5 11 8a2 2 0 0 1 2.8 0l4.2 4.2a2 2 0 0 1 0 2.8l-3.6 3.6H8.1a2 2 0 0 1-1.4-.58L4.5 16.3a2 2 0 0 1 0-1.8z" />
    <path d="M11 8l5 5" />
    <line x1="10" y1="20" x2="18" y2="20" />
  </svg>
);

const IconMaximise = props => (
  <svg {...iconCommonProps} {...props}>
    <polyline points="9 3 3 3 3 9" />
    <line x1="3" y1="3" x2="9" y2="9" />
    <polyline points="15 21 21 21 21 15" />
    <line x1="15" y1="15" x2="21" y2="21" />
  </svg>
);

const IconMinimise = props => (
  <svg {...iconCommonProps} {...props}>
    <polyline points="9 21 3 21 3 15" />
    <line x1="3" y1="21" x2="9" y2="15" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="15" y1="9" x2="21" y2="3" />
  </svg>
);
const htmlToPlainText = html => {
  if (!html || typeof html !== 'string') return '';
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return doc.body?.textContent?.trim() || '';
  } catch (error) {
    return html;
  }
};

const escapeHtml = text =>
  String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const escapeAttribute = value => escapeHtml(value).replace(/`/g, '&#96;');

const buildStyledTextHtml = (text, styles) => {
  if (!text) return '';
  const safe = escapeHtml(text);
  if (!Array.isArray(styles) || styles.includes('normal')) return safe;
  let styled = safe;
  if (styles.includes('bold')) styled = `<strong>${styled}</strong>`;
  if (styles.includes('italic')) styled = `<em>${styled}</em>`;
  if (styles.includes('underline')) styled = `<u>${styled}</u>`;
  return styled;
};

const LAYOUT_VARIANTS = {
  DEFAULT: 'default',
  REGMAIL: 'regmail',
};

const resolveLayoutVariant = settings =>
  `${settings?.regmailEnabled ? LAYOUT_VARIANTS.REGMAIL : LAYOUT_VARIANTS.DEFAULT}|${
    settings?.includeWalletButtons ? 'wallet-on' : 'wallet-off'
  }|${settings?.includeCalendarButton ? 'calendar-on' : 'calendar-off'}`;

const selectVariantState = (visualEditor, layoutKey) => {
  if (!visualEditor || typeof visualEditor !== 'object') {
    return { key: layoutKey, data: null };
  }

  if (visualEditor.variants && typeof visualEditor.variants === 'object') {
    if (visualEditor.variants[layoutKey]) {
      return { key: layoutKey, data: visualEditor.variants[layoutKey] };
    }
    const parts = layoutKey.split('|');
    while (parts.length > 1) {
      parts.pop();
      const fallbackKey = parts.join('|');
      if (visualEditor.variants[fallbackKey]) {
        return { key: fallbackKey, data: visualEditor.variants[fallbackKey] };
      }
    }
    return { key: layoutKey, data: null };
  }

  return {
    key: visualEditor.activeVariant || layoutKey,
    data: visualEditor,
  };
};

const upsertVariantState = (visualEditor, layoutKey, payload) => {
  const base =
    visualEditor && typeof visualEditor === 'object'
      ? { ...visualEditor }
      : {};
  const variants = {
    ...(base.variants && typeof base.variants === 'object'
      ? base.variants
      : {}),
    [layoutKey]: payload,
  };
  return {
    ...base,
    variants,
    activeVariant: layoutKey,
    html: payload?.html,
    rawHtml: payload?.rawHtml,
    css: payload?.css,
    savedAt: payload?.savedAt,
    version: payload?.version,
  };
};

const removeVariantState = (visualEditor, layoutKey) => {
  if (!visualEditor || typeof visualEditor !== 'object' || !layoutKey) {
    return visualEditor || null;
  }

  const hasVariants =
    visualEditor.variants && typeof visualEditor.variants === 'object';
  if (!hasVariants) {
    if (visualEditor.activeVariant === layoutKey) {
      const nextState = { ...visualEditor };
      delete nextState.activeVariant;
      return Object.keys(nextState).length > 0 ? nextState : null;
    }
    return visualEditor;
  }

  if (!visualEditor.variants[layoutKey]) {
    return visualEditor;
  }

  const nextVariants = { ...visualEditor.variants };
  delete nextVariants[layoutKey];
  if (!Object.keys(nextVariants).length) {
    return null;
  }

  return {
    ...visualEditor,
    variants: nextVariants,
    activeVariant: nextVariants[visualEditor.activeVariant]
      ? visualEditor.activeVariant
      : Object.keys(nextVariants)[0],
  };
};

const convertPlainTextToHtml = text => {
  const trimmed = (text || '').trim();
  if (!trimmed) return '';
  const paragraphs = trimmed
    .split(/\n{2,}/)
    .map(segment => `<p>${escapeHtml(segment).replace(/\n/g, '<br />')}</p>`);
  return paragraphs.join('');
};

const buildDefaultParagraphHtmlFromText = (text, settings) => {
  const base = convertPlainTextToHtml(text);
  if (!base) return '';
  const styleParts = [
    `font-size: ${settings?.bodyFontSize || 14}pt`,
    `line-height: ${settings?.paragraphLineHeight || 1.6}`,
    'margin: 0 0 16px 0',
    `color: ${settings?.bodyTextColor || '#1f2937'}`,
    `font-family: ${getFontStack(settings?.templateFontFamily)}`,
  ];
  const style = styleParts.join('; ');
  return base.replace(/<p>/g, `<p style="${style}">`);
};

const prepareAiHtml = text => {
  const trimmed = (text || '').trim();
  if (!trimmed) return '';
  if (/<\s*(p|ul|ol|li|table|div|section|article|header|footer|blockquote|h[1-6]|span|br)/i.test(trimmed)) {
    return trimmed;
  }
  return convertPlainTextToHtml(trimmed);
};

const BLOCK_LEVEL_TAGS = new Set([
  'p',
  'div',
  'section',
  'article',
  'header',
  'footer',
  'blockquote',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'table',
  'tbody',
  'thead',
  'tfoot',
  'tr',
  'td',
  'th',
  'figure',
  'figcaption',
  'aside',
  'main',
]);

const normaliseAiInlineHtml = text => {
  const trimmed = (text || '').trim();
  if (!trimmed) return '';

  const toLineBreaks = value => escapeHtml(value).replace(/\n/g, '<br />');

  if (!/<[a-z][\s\S]*>/i.test(trimmed)) {
    return toLineBreaks(trimmed);
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${trimmed}</div>`, 'text/html');
    const segments = [];
    const pushSegment = segment => {
      const clean = segment?.trim();
      if (clean) segments.push(clean);
    };

    doc.body.childNodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        const textContent = node.textContent?.replace(/\s+/g, ' ').trim();
        if (textContent) {
          pushSegment(toLineBreaks(textContent));
        }
        return;
      }

      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const tag = node.tagName.toLowerCase();

      if (tag === 'ul' || tag === 'ol') {
        const bullets = Array.from(node.children)
          .filter(child => child.tagName && child.tagName.toLowerCase() === 'li')
          .map(child => child.innerHTML.trim())
          .filter(Boolean);
        if (bullets.length) {
          pushSegment(
            bullets
              .map(item => `&bull;&nbsp;${item}`)
              .join('<br />')
          );
        }
        return;
      }

      if (BLOCK_LEVEL_TAGS.has(tag)) {
        pushSegment(node.innerHTML.trim() || node.textContent.trim());
        return;
      }

      pushSegment(node.outerHTML.trim());
    });

    if (!segments.length) {
      const fallback = doc.body.textContent || '';
      return toLineBreaks(fallback);
    }

    return segments.join('<br /><br />');
  } catch (error) {
    return toLineBreaks(trimmed);
  }
};

const buildDomPath = (root, node) => {
  if (!root || !node) return null;
  const path = [];
  let current = node;
  while (current && current !== root) {
    const parent = current.parentNode;
    if (!parent) return null;
    const index = Array.prototype.indexOf.call(parent.childNodes, current);
    if (index < 0) return null;
    path.unshift(index);
    current = parent;
  }
  return current === root ? path : null;
};

const resolveDomPath = (root, path = []) => {
  if (!root) return null;
  let current = root;
  for (const index of path) {
    if (!current || !current.childNodes || index >= current.childNodes.length) {
      return null;
    }
    current = current.childNodes[index];
  }
  return current || null;
};

const serializeRange = (root, range) => {
  if (!root || !range) return null;
  const startPath = buildDomPath(root, range.startContainer);
  const endPath = buildDomPath(root, range.endContainer);
  if (!startPath || !endPath) return null;
  return {
    startPath,
    startOffset: range.startOffset || 0,
    endPath,
    endOffset: range.endOffset || 0,
    collapsed: range.collapsed,
  };
};

const deserializeRange = (root, info, doc = document) => {
  if (!root || !info || !Array.isArray(info.startPath) || !Array.isArray(info.endPath)) {
    return null;
  }
  const startNode = resolveDomPath(root, info.startPath);
  const endNode = resolveDomPath(root, info.endPath);
  if (!startNode || !endNode) return null;
  try {
    const range = doc.createRange();
    const clampOffset = (node, offset) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const len = node.textContent ? node.textContent.length : 0;
        return Math.max(0, Math.min(offset, len));
      }
      const len = node.childNodes ? node.childNodes.length : 0;
      return Math.max(0, Math.min(offset, len));
    };
    range.setStart(startNode, clampOffset(startNode, info.startOffset || 0));
    range.setEnd(endNode, clampOffset(endNode, info.endOffset || 0));
    return range;
  } catch (error) {
    return null;
  }
};

const buildStructuredReplacement = (originalHtml, aiText) => {
  const preparedText = prepareAiHtml(aiText);
  const inlineText = normaliseAiInlineHtml(aiText);

  if (!originalHtml) {
    return preparedText || inlineText;
  }

  try {
    const parser = new DOMParser();
    const originalDoc = parser.parseFromString(`<div>${originalHtml}</div>`, 'text/html');
    const originalChildren = Array.from(originalDoc.body.childNodes || []);

    if (!originalChildren.length) {
      return preparedText || inlineText;
    }

    const preparedDoc = parser.parseFromString(`<div>${preparedText || inlineText}</div>`, 'text/html');
    const replacementNodes = Array.from(preparedDoc.body.childNodes || []).filter(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent && node.textContent.trim().length;
      }
      return node.nodeType === Node.ELEMENT_NODE;
    });

    if (!replacementNodes.length) {
      return inlineText;
    }

    const resultDoc = parser.parseFromString('<div></div>', 'text/html');
    let replacementIndex = 0;

    originalChildren.forEach(child => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const clone = child.cloneNode(true);
        const replacement = replacementNodes[Math.min(replacementIndex, replacementNodes.length - 1)];
        replacementIndex += 1;

        if (replacement) {
          if (replacement.nodeType === Node.ELEMENT_NODE) {
            const replacementClone = replacement.cloneNode(true);
            const cloneTag = clone.tagName?.toLowerCase();
            const replacementTag = replacementClone.tagName?.toLowerCase();

            if (cloneTag && replacementTag && cloneTag === replacementTag) {
              clone.innerHTML = replacementClone.innerHTML;
            } else {
              clone.innerHTML = '';
              clone.appendChild(replacementClone);
            }
          } else if (replacement.nodeType === Node.TEXT_NODE) {
            clone.innerHTML = escapeHtml(replacement.textContent || '');
          }
        }

        resultDoc.body.appendChild(clone);
      } else if (child.nodeType === Node.TEXT_NODE) {
        resultDoc.body.appendChild(child.cloneNode(true));
      }
    });

    // Append any remaining replacement nodes that didn't get used
    for (let i = replacementIndex; i < replacementNodes.length; i += 1) {
      const remaining = replacementNodes[i];
      if (remaining.nodeType === Node.ELEMENT_NODE) {
        resultDoc.body.appendChild(remaining.cloneNode(true));
      } else if (remaining.nodeType === Node.TEXT_NODE) {
        const span = resultDoc.createElement('span');
        span.innerHTML = escapeHtml(remaining.textContent || '');
        resultDoc.body.appendChild(span);
      }
    }

    return resultDoc.body.innerHTML || preparedText || inlineText;
  } catch (error) {
    return preparedText || inlineText;
  }
};

const captureSelectionRangeInfo = editor => {
  if (!editor || !editor.s) return null;
  const selection = editor.s.sel;
  if (!selection || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  if (!range || range.collapsed) return null;
  return serializeRange(editor.editor, range);
};

const applyRangeInfoToEditor = (editor, rangeInfo) => {
  if (!editor || !rangeInfo) return false;
  const root = editor.editor;
  const doc = editor.editorDocument || editor.ownerDocument || document;
  const range = deserializeRange(root, rangeInfo, doc);
  if (!range) return false;
  try {
    if (typeof editor.s.selectRange === 'function') {
      editor.s.selectRange(range);
      return true;
    }
    const selection = doc.getSelection ? doc.getSelection() : null;
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
      return true;
    }
  } catch (error) {
    return false;
  }
  return false;
};

const wrapAiBlockHtml = (modeId, html) => {
  if (!html || typeof html !== 'string') return '';
  const safeMode = escapeAttribute(modeId || 'ai-block');
  return `
    <section data-ai-generated="true" data-ai-mode="${safeMode}">
      ${html}
    </section>
  `;
};

const SANITISED_ALLOWED_TAGS = new Set([
  'p',
  'br',
  'strong',
  'em',
  'b',
  'i',
  'u',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'blockquote',
  'a',
]);

const sanitizeHref = href => {
  if (typeof href !== 'string') return '';
  const trimmed = href.trim();
  if (!trimmed) return '';
  if (/^javascript:/i.test(trimmed)) return '';
  return trimmed;
};

const sanitizeAiHtml = (raw, { allowStructure = true, allowFullDocument = false } = {}) => {
  const text = (raw || '').trim();
  if (!text) return '';

  if (allowFullDocument) {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');
      if (doc?.body) {
        doc.querySelectorAll('script, iframe, object').forEach(node => node.remove());
        return doc.body.innerHTML.trim();
      }
    } catch {
      // fall through to returning trimmed text
    }
    return text;
  }

  if (!allowStructure) {
    const plain = htmlToPlainText(text).replace(/\r\n/g, '\n').trim();
    if (!plain) return '';
    return escapeHtml(plain)
      .replace(/\n{2,}/g, '<br /><br />')
      .replace(/\n/g, '<br />');
  }

  const parser = new DOMParser();
  let doc;
  try {
    doc = parser.parseFromString(`<div>${text}</div>`, 'text/html');
  } catch (error) {
    return convertPlainTextToHtml(text);
  }
  if (!doc?.body) {
    return convertPlainTextToHtml(text);
  }

  const cleanNode = node => {
    if (!node) return null;
    if (node.nodeType === Node.TEXT_NODE) {
      const value = node.textContent || '';
      if (!value.trim()) return null;
      return doc.createTextNode(value);
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return null;
    }

    const tag = node.tagName?.toLowerCase?.() || '';

    if (!allowStructure || !SANITISED_ALLOWED_TAGS.has(tag)) {
      const fragment = doc.createDocumentFragment();
      Array.from(node.childNodes || []).forEach(child => {
        const cleaned = cleanNode(child);
        if (cleaned) {
          fragment.appendChild(cleaned);
        }
      });
      return fragment;
    }

    const element = doc.createElement(tag);

    if (tag === 'a') {
      const href = sanitizeHref(node.getAttribute('href'));
      if (href) {
        element.setAttribute('href', href);
      }
      if (node.hasAttribute('target')) {
        element.setAttribute('target', node.getAttribute('target'));
      }
      if (node.hasAttribute('rel')) {
        element.setAttribute('rel', node.getAttribute('rel'));
      }
    }

    Array.from(node.childNodes || []).forEach(child => {
      const cleaned = cleanNode(child);
      if (cleaned) {
        element.appendChild(cleaned);
      }
    });

    if (!element.childNodes.length) {
      return null;
    }

    return element;
  };

  const fragment = doc.createDocumentFragment();
  Array.from(doc.body.childNodes || []).forEach(child => {
    const cleaned = cleanNode(child);
    if (cleaned) {
      fragment.appendChild(cleaned);
    }
  });

  if (!fragment.childNodes.length) {
    return convertPlainTextToHtml(text);
  }

  const container = doc.createElement('div');
  container.appendChild(fragment);
  return container.innerHTML.trim();
};

const normaliseForMatch = value =>
  htmlToPlainText(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const fuzzyMatch = (a, b) => {
  if (!a || !b) return false;
  if (a === b) return true;
  if (a.length >= 12 && b.length >= 12) {
    return a.includes(b) || b.includes(a);
  }
  return false;
};

const matchSelectionToSetting = (selection, settings) => {
  const normalizedSelection = normaliseForMatch(selection);
  if (!normalizedSelection) return null;

  const directFields = [
    { key: 'salutationInput', label: 'Salutation' },
    { key: 'footerText', label: 'Footer text' },
    { key: 'topPaddingText', label: 'Banner text' },
    { key: 'organizingTeamName', label: 'Organising team name' },
    { key: 'socialTextInput', label: 'Social prompt' },
    { key: 'walletIntroText', label: 'Wallet intro' },
    { key: 'walletInstructionsText', label: 'Wallet instructions' },
    { key: 'calendarButtonText', label: 'Calendar button text' },
  ];

  for (const field of directFields) {
    const candidateValue = settings?.[field.key];
    const normalizedField = normaliseForMatch(candidateValue);
    if (
      normalizedField &&
      (normalizedField === normalizedSelection || fuzzyMatch(normalizedField, normalizedSelection))
    ) {
      return {
        id: `field:${field.key}`,
        type: 'field',
        key: field.key,
        label: field.label,
      };
    }
  }

  if (Array.isArray(settings?.sectionTitles)) {
    for (let i = 0; i < settings.sectionTitles.length; i += 1) {
      const title = settings.sectionTitles[i];
      const normalizedField = normaliseForMatch(title?.text);
      if (
        normalizedField &&
        (normalizedField === normalizedSelection || fuzzyMatch(normalizedField, normalizedSelection))
      ) {
        return {
          id: `sectionTitle:${i}`,
          type: 'sectionTitle',
          key: 'sectionTitles',
          index: i,
          label: title?.text || `Section heading ${i + 1}`,
        };
      }
    }
  }

  if (Array.isArray(settings?.ctaButtons)) {
    for (let i = 0; i < settings.ctaButtons.length; i += 1) {
      const button = settings.ctaButtons[i];
      const normalizedField = normaliseForMatch(button?.text);
      if (
        normalizedField &&
        (normalizedField === normalizedSelection || fuzzyMatch(normalizedField, normalizedSelection))
      ) {
        return {
          id: `cta:${i}`,
          type: 'cta',
          key: 'ctaButtons',
          index: i,
          label: button?.text || `CTA ${i + 1}`,
        };
      }
    }
  }

  return null;
};

const createCtaBlockHtml = (button, { fontFamily, fontSizePt, lineHeightValue, textColor }) => {
  const safeFontFamily = fontFamily || 'Arial, sans-serif';
  const safeFontSize = fontSizePt || '16pt';
  const safeLineHeight = lineHeightValue || '1.6';
  const safeTextColor = textColor || '#1f2937';
  const background = button.backgroundColor || '#2563eb';
  const foreground = button.textColor || '#ffffff';
  const fontSize = Number.isFinite(Number(button.fontSize)) ? Number(button.fontSize) : 16;
  const paddingX = Number.isFinite(Number(button.paddingX)) ? Number(button.paddingX) : 24;
  const paddingY = Number.isFinite(Number(button.paddingY)) ? Number(button.paddingY) : 12;
  const radius = Number.isFinite(Number(button.borderRadius)) ? Number(button.borderRadius) : 999;
  const safeText = escapeHtml(button.text || 'Call to action');
  const safeUrl = escapeAttribute(button.url || '#');

  return `
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" data-cta-block="true" data-cta-id="${button.id}" style="width:100%; max-width:600px; margin:0 auto; background:#ffffff; border-radius:16px; overflow:hidden;">
      <tbody>
        <tr>
          <td style="padding:28px 24px; font-family:${safeFontFamily}; font-size:${safeFontSize}; line-height:${safeLineHeight}; color:${safeTextColor};">
            <h2 style="margin:0 0 12px;font-size:22pt;line-height:1.3;text-align:center;">Quick reminder</h2>
            <p style="margin:0 0 20px;text-align:center;">Encourage recipients to take the next step with a clear CTA.</p>
            <div style="text-align:center;">
              <a data-cta-id="${button.id}" href="${safeUrl}" style="display:inline-block;background:${background};color:${foreground};text-decoration:none;padding:${paddingY}px ${paddingX}px;border-radius:${radius}px;font-weight:600;font-size:${fontSize}pt;">
                ${safeText}
              </a>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  `;
};

const AI_PRESETS = [
  {
    id: 'improve',
    label: 'Improve tone',
    description: 'Polish grammar, clarity, and flow while keeping the original intent.',
    scope: 'selection',
    insertion: 'replace',
  },
  {
    id: 'summarize',
    label: 'Summarise',
    description: 'Create a concise summary suitable for a highlight paragraph.',
    scope: 'selection',
    insertion: 'replace',
  },
  {
    id: 'expand',
    label: 'Expand',
    description: 'Add persuasive detail without changing the intent.',
    scope: 'selection',
    insertion: 'replace',
  },
  {
    id: 'translate',
    label: 'Translate',
    description: 'Translate the selected content using one of the predefined languages.',
    scope: 'selection',
    insertion: 'replace',
    requiresLanguage: true,
  },
  {
    id: 'rewrite_full',
    label: 'Rewrite email',
    description: 'Generate a refreshed version of the full email body inside a new block.',
    scope: 'full-email',
    insertion: 'block',
  },
  {
    id: 'outline',
    label: 'Outline email',
    description: 'Create a structured outline with headings and bullet points.',
    scope: 'section-outline',
    insertion: 'block',
  },
];

const AiAssistantPanel = ({
  open,
  mode,
  presets,
  activePreset,
  onModeChange,
  selectionText,
  promptValue,
  onPromptChange,
  onGenerate,
  onInsert,
  onReplace,
  onClose,
  status,
  preview,
  isGenerating,
  onUseSelection,
  canReplace,
  languages,
  selectedLanguage,
  onLanguageChange,
  snippetCategories,
  selectedSnippets,
  onToggleSnippet,
  progress,
  history,
  onSelectHistory,
  restrictToSelection,
  onToggleRestrict,
  blocks,
  selectedBlockId,
  onSelectBlock,
  onTranslateFullEmail,
}) => {
  if (!open) return null;

  const languageOptions = Array.isArray(languages) ? languages : [];
  const snippetsByCategory = Array.isArray(snippetCategories) ? snippetCategories : [];
  const showingLanguagePicker =
    languageOptions.length > 0 && (activePreset?.requiresLanguage || languageOptions.length > 1);

  const blockOptions = Array.isArray(blocks) ? blocks : [];

  const hasHistory = Array.isArray(history) && history.length > 0;
  const hasSelection = Boolean(selectionText && selectionText.trim());

  return (
    <div className="fixed inset-0 z-[118] flex items-stretch justify-end bg-slate-900/60 px-4 py-6">
      <div className="relative flex h-full w-full max-w-3xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">AI writing assistant</h2>
            <p className="text-sm text-slate-500">
              {activePreset?.description || 'Select a preset to guide the AI response.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-300 px-3 py-1 text-sm font-semibold text-slate-600 hover:border-slate-400"
          >
            Close
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5 text-sm text-slate-600">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-inner">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-sm font-semibold text-slate-700">Selection preview</h3>
                <p className="text-xs text-slate-500">Capture text from the editor to focus the AI on specific content.</p>
              </div>
              <button
                type="button"
                onClick={onUseSelection}
                className="inline-flex items-center gap-1 rounded-full border border-primary px-3 py-1 text-xs font-semibold text-primary transition hover:bg-primary/10"
              >
                Use current selection
              </button>
              {activePreset?.id === 'translate' && typeof onTranslateFullEmail === 'function' && (
                <button
                  type="button"
                  onClick={onTranslateFullEmail}
                  disabled={isGenerating}
                  className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold transition ${
                    isGenerating
                      ? 'cursor-not-allowed border-slate-200 text-slate-400'
                      : 'border-emerald-500 text-emerald-600 hover:bg-emerald-50'
                  }`}
                >
                  Translate entire email
                </button>
              )}
            </div>
            <div className="mt-3 max-h-44 overflow-y-auto whitespace-pre-wrap rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
              {selectionText ? selectionText : 'No selection captured. The AI will use the broader email context.'}
            </div>
            <label
              className={`mt-3 flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold transition ${
                restrictToSelection
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-slate-200 text-slate-500'
              } ${!hasSelection ? 'opacity-60' : ''}`}
            >
              <input
                type="checkbox"
                className="rounded border-slate-300"
                checked={restrictToSelection}
                onChange={onToggleRestrict}
                disabled={!hasSelection}
              />
              <span>Only work on captured selection</span>
              {!hasSelection && (
                <span className="ml-auto text-rose-400">Capture text first</span>
              )}
            </label>
          </div>

          {blockOptions.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white/70 p-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-slate-700">Target blocks</h3>
                  <p className="text-xs text-slate-500">
                    Choose a specific block to highlight and replace. Leave unselected to rely on manual selection.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onSelectBlock?.('')}
                  className="inline-flex items-center rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-400"
                >
                  Clear target
                </button>
              </div>
              <div className="mt-3 space-y-2">
                <label
                  className={`flex items-start gap-2 rounded-xl border px-3 py-2 text-xs transition ${
                    !selectedBlockId
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-slate-200 text-slate-600 hover:border-primary/40'
                  }`}
                >
                  <input
                    type="radio"
                    name="ai-target-block"
                    value=""
                    checked={!selectedBlockId}
                    onChange={() => onSelectBlock?.('')}
                    className="mt-1"
                  />
                  <div>
                    <p className="font-semibold">Use manual selection</p>
                    <p className="mt-1 text-[11px] text-slate-500">
                      Operate on the highlighted selection captured from the editor.
                    </p>
                  </div>
                </label>
                {blockOptions.map(block => {
                  const active = selectedBlockId === block.id;
                  return (
                    <label
                      key={block.id}
                      className={`flex items-start gap-2 rounded-xl border px-3 py-2 text-xs transition ${
                        active
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-slate-200 text-slate-600 hover:border-primary/40'
                      }`}
                    >
                      <input
                        type="radio"
                        name="ai-target-block"
                        value={block.id}
                        checked={active}
                        onChange={() => onSelectBlock?.(block.id)}
                        className="mt-1"
                      />
                      <div>
                        <p className="font-semibold">{block.label}</p>
                        {block.excerpt ? (
                          <p className="mt-1 text-[11px] text-slate-500">{block.excerpt}</p>
                        ) : null}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <h3 className="text-sm font-semibold text-slate-700">Choose a preset</h3>
            <div className="mt-2 flex flex-wrap gap-2">
              {presets.map(preset => {
                const active = preset.id === mode;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => onModeChange(preset.id)}
                    className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
                      active
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-slate-200 text-slate-600 hover:border-primary/40 hover:text-primary'
                    }`}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
          </div>

          {showingLanguagePicker && (
            <div>
              <h3 className="text-sm font-semibold text-slate-700">Target language</h3>
              <div className="mt-2 flex flex-wrap gap-2">
                {languageOptions.map(lang => {
                  const active = lang.id === selectedLanguage;
                  return (
                    <button
                      key={lang.id || lang.label}
                      type="button"
                      onClick={() => onLanguageChange(lang.id)}
                      className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
                        active
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-slate-200 text-slate-600 hover:border-primary/40 hover:text-primary'
                      }`}
                      title={lang.prompt}
                    >
                      {lang.label || lang.id}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {snippetsByCategory.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-700">Prompt snippets</h3>
              {snippetsByCategory.map(category => (
                <div key={category.id || category.label} className="rounded-xl border border-slate-200 p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                        {category.label || category.id}
                      </p>
                      {category.description ? (
                        <p className="text-xs text-slate-500 mt-1">{category.description}</p>
                      ) : null}
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(category.options || []).map(option => {
                      const optionId = option.id || option.label;
                      const active = selectedSnippets.includes(optionId);
                      return (
                        <button
                          key={optionId}
                          type="button"
                          onClick={() => onToggleSnippet(optionId)}
                          className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
                            active
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-slate-200 text-slate-600 hover:border-primary/40 hover:text-primary'
                          }`}
                          title={option.prompt}
                        >
                          {option.label || optionId}
                        </button>
                      );
                    })}
                    {(!category.options || category.options.length === 0) && (
                      <span className="text-xs text-slate-500">No snippets configured.</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-slate-700" htmlFor="ai-custom-instructions">
              Additional instructions (optional)
            </label>
            <textarea
              id="ai-custom-instructions"
              value={promptValue}
              onChange={event => onPromptChange(event.target.value)}
              placeholder="e.g. Make the tone sound more upbeat and mention early-bird pricing."
              className="mt-2 min-h-[96px] w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
            <div className="mt-3 flex items-center justify-between gap-3">
              <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={`h-full transition-all ${isGenerating ? 'bg-primary' : 'bg-emerald-400'}`}
                  style={{ width: `${isGenerating ? Math.max(progress, 12) : progress}%` }}
                />
              </div>
              <button
                type="button"
                onClick={() => onGenerate(mode)}
                disabled={isGenerating}
                className={`rounded-lg px-4 py-2 text-sm font-semibold text-white ${
                  isGenerating ? 'bg-slate-400' : 'bg-primary hover:bg-primary/90'
                }`}
              >
                {isGenerating ? 'Generating…' : 'Generate'}
              </button>
            </div>
            {status.message && (
              <p
                className={`mt-2 text-xs ${
                  status.state === 'error'
                    ? 'text-rose-500'
                    : status.state === 'success'
                    ? 'text-emerald-600'
                    : 'text-slate-500'
                }`}
              >
                {status.message}
              </p>
            )}
          </div>

          <div>
            <h3 className="text-sm font-semibold text-slate-700">AI draft</h3>
            <div className="mt-2 min-h-[140px] rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700 shadow-inner whitespace-pre-wrap">
              {preview || 'Run “Generate” to see suggestions here.'}
            </div>
            <div className="mt-3 flex flex-wrap justify-end gap-2">
              {canReplace && activePreset?.insertion === 'replace' && (
                <button
                  type="button"
                  onClick={onReplace}
                  disabled={!preview || isGenerating}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold ${
                    !preview || isGenerating
                      ? 'cursor-not-allowed bg-slate-200 text-slate-500'
                      : 'bg-white text-primary border border-primary hover:bg-primary/10'
                  }`}
                >
                  Replace selection
                </button>
              )}
              <button
                type="button"
                onClick={onInsert}
                disabled={!preview || isGenerating}
                className={`rounded-lg px-4 py-2 text-sm font-semibold text-white ${
                  !preview || isGenerating
                    ? 'cursor-not-allowed bg-slate-200 text-slate-500'
                    : 'bg-primary hover:bg-primary/90'
                }`}
              >
                Insert into email
              </button>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-slate-700">History</h3>
            {!hasHistory && (
              <p className="mt-2 text-xs text-slate-500">No previous results yet.</p>
            )}
            {hasHistory && (
              <div className="mt-2 space-y-2">
                {history.map(item => (
                  <div
                    key={item.id}
                    className="rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-600 bg-slate-50 flex items-center justify-between gap-3"
                  >
                    <div className="space-y-1">
                      <p className="font-semibold text-slate-700">
                        {item.preset?.label || item.mode}
                        {item.languageLabel ? ` · ${item.languageLabel}` : ''}
                      </p>
                      <p className="text-[11px] text-slate-500">
                        {new Date(item.timestamp).toLocaleString()} · {(item.excerpt || '').slice(0, 120)}
                        {(item.excerpt || '').length > 120 ? '…' : ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => onSelectHistory(item)}
                      className="whitespace-nowrap rounded border border-primary px-3 py-1 font-semibold text-primary hover:bg-primary/10"
                    >
                      Load draft
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const VisualEditorSection = ({
  settings,
  onUpdate,
  globalSettings,
  showNotification,
  onRegisterPersist,
  previewDevice,
  onPreviewDeviceChange,
}) => {
  const joditRef = useRef(null);
  const workspaceRef = useRef(null);
  const autoSaveTimeout = useRef(null);
  const persistVisualEditorRef = useRef(() => null);
  const contentRef = useRef('');
  const lastSyncedVisualRef = useRef(null);
  const aiSelectionRef = useRef(null);
  const aiCaretRef = useRef(null);
  const linkSelectionRef = useRef(null);
  const toolbarRef = useRef(null);
  const linkButtonRef = useRef(null);
  const linkPopoverRef = useRef(null);
  const progressTimerRef = useRef(null);
  const prevSettingsRef = useRef(settings);
  const aiBlockHighlightRef = useRef(null);
  const aiBlocksRef = useRef(new Map());
  const hydratedVariantKeysRef = useRef(new Set());

  const [editorReady, setEditorReady] = useState(false);
  const regmailEnabled = Boolean(settings?.regmailEnabled);
  const includeWalletButtons = Boolean(settings?.includeWalletButtons);
  const includeCalendarButton = Boolean(settings?.includeCalendarButton);
  const layoutVariant = useMemo(
    () =>
      resolveLayoutVariant({
        regmailEnabled,
        includeWalletButtons,
        includeCalendarButton,
      }),
    [regmailEnabled, includeWalletButtons, includeCalendarButton]
  );
  const layoutVariantRef = useRef(layoutVariant);
  const resolvedVariantState = useMemo(
    () => selectVariantState(settings?.visualEditor, layoutVariant),
    [settings?.visualEditor, layoutVariant]
  );
  const [saveStatus, setSaveStatus] = useState({
    state: 'idle',
    savedAt: resolvedVariantState.data?.savedAt || null,
  });
  const [relativeNow, setRelativeNow] = useState(Date.now());
  const [historyState, setHistoryState] = useState({ canUndo: false, canRedo: false });
  const [isAiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiMode, setAiMode] = useState(AI_PRESETS[0].id);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiStatus, setAiStatus] = useState({ state: 'idle', message: '' });
  const [aiPreview, setAiPreview] = useState('');
  const [aiSelectionSummary, setAiSelectionSummary] = useState({
    text: '',
    html: '',
    sectionId: null,
    scope: 'selection',
    blockId: null,
    rangeInfo: null,
    fieldPath: null,
  });
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [selectedSnippets, setSelectedSnippets] = useState([]);
  const [aiHistory, setAiHistory] = useState([]);
  const [aiProgress, setAiProgress] = useState(0);
  const [aiRestrictToSelection, setAiRestrictToSelection] = useState(false);
  const [aiBlockOptions, setAiBlockOptions] = useState([]);
  const [aiSelectedBlockId, setAiSelectedBlockId] = useState('');
  const [formatState, setFormatState] = useState({
    bold: false,
    italic: false,
    underline: false,
    strike: false,
    orderedList: false,
    unorderedList: false,
    alignment: 'left',
    blockTag: 'p',
    linkActive: false,
    fontSize: '',
  });
  const [fontSizeDraft, setFontSizeDraft] = useState('');
  const [viewMode, setViewMode] = useState('edit');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [linkToolState, setLinkToolState] = useState({
    isOpen: false,
    mode: 'web',
    displayText: '',
    address: '',
  });
  const [linkToolError, setLinkToolError] = useState('');
  const [linkPopoverCoords, setLinkPopoverCoords] = useState({ top: 0, left: 0 });
  const normalizedDevice = useMemo(() => ensureDeviceKey(previewDevice), [previewDevice]);

  const primaryFontName = useMemo(
    () => settings?.templateFontFamily || DEFAULT_FONT_FAMILY,
    [settings?.templateFontFamily]
  );

  const fontStack = useMemo(() => getFontStack(primaryFontName), [primaryFontName]);

  const fontSizeNumber = useMemo(() => {
    const size = Number(settings?.bodyFontSize);
    return Number.isFinite(size) && size > 0 ? size : 16;
  }, [settings?.bodyFontSize]);

  const fontSizePt = useMemo(() => `${fontSizeNumber}pt`, [fontSizeNumber]);

  const lineHeightNumeric = useMemo(() => {
    const lh = Number(settings?.paragraphLineHeight);
    if (Number.isFinite(lh) && lh > 0) return lh;
    return 1.6;
  }, [settings?.paragraphLineHeight]);

  const lineHeightValue = useMemo(
    () => lineHeightNumeric.toFixed(2),
    [lineHeightNumeric]
  );

  const aiConfig = useMemo(
    () => ensureAiConfig(globalSettings?.aiConfig),
    [globalSettings?.aiConfig]
  );

  const snippetLookup = useMemo(() => {
    const map = new Map();
    (aiConfig.snippetCategories || []).forEach(category => {
      (category.options || []).forEach(option => {
        if (option?.id) {
          map.set(option.id, option);
        }
      });
    });
    return map;
  }, [aiConfig]);

  const activePreset = useMemo(
    () => AI_PRESETS.find(preset => preset.id === aiMode) || AI_PRESETS[0],
    [aiMode]
  );

  useEffect(() => {
    const languages = Array.isArray(aiConfig.languages) ? aiConfig.languages : [];
    if (!languages.length) {
      setSelectedLanguage('');
      return;
    }
    setSelectedLanguage(prev => {
      if (prev && languages.some(language => language.id === prev)) {
        return prev;
      }
      return languages[0].id;
    });
  }, [aiConfig]);

  useEffect(() => {
    setSelectedSnippets(prev => prev.filter(id => snippetLookup.has(id)));
  }, [snippetLookup]);

  useEffect(() => {
    return () => {
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
      }
    };
  }, []);

  const handleToggleSnippet = useCallback(id => {
    if (!id) return;
    setSelectedSnippets(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  }, []);

  const handleRestrictToggle = useCallback(() => {
    setAiRestrictToSelection(prev => !prev);
  }, []);

  const bodyColor = settings?.bodyTextColor || '#1f2937';
  const backgroundColor = settings?.bodyBackgroundColor || '#f8fafc';
  const linkColor = settings?.linkColor || '#2563eb';
  const dividerColor = settings?.dividerColor || '#d1d5db';

  const iframeStyles = useMemo(
    () => `
      body { background:${backgroundColor}; font-family:${fontStack}; font-size:${fontSizePt}; line-height:${lineHeightValue}; color:${bodyColor}; }
      a { color:${linkColor}; }
      hr { border:none; border-top:1px solid ${dividerColor}; }
      table { border-collapse:collapse; }
      img {
        border:0;
        height:auto;
        line-height:100%;
        outline:none;
        text-decoration:none;
        -ms-interpolation-mode:bicubic;
        max-width:100%;
        display:block;
      }
      body, table, td, p, a, li, blockquote {
        -webkit-text-size-adjust:100%;
        -ms-text-size-adjust:100%;
      }
      .mobile-full {
        width:100% !important;
        height:auto !important;
      }
      .mobile-padding {
        padding:10px !important;
      }
      @media only screen and (max-width:600px) {
        .stack td {
          display:block !important;
          width:100% !important;
          box-sizing:border-box !important;
        }
      }
    `,
    [backgroundColor, bodyColor, fontStack, fontSizePt, lineHeightValue, linkColor, dividerColor]
  );

  const blockLibrary = useMemo(
    () =>
      buildBlockLibrary({
        fontFamily: fontStack,
        fontSize: fontSizePt,
        lineHeight: lineHeightValue,
        textColor: bodyColor,
        dividerColor,
      }),
    [fontStack, fontSizePt, lineHeightValue, bodyColor, dividerColor]
  );

  const blockLookup = useMemo(() => {
    const map = new Map();
    blockLibrary.forEach(block => map.set(block.id, block));
    return map;
  }, [blockLibrary]);

  const previewPreset = DEVICE_PRESETS[normalizedDevice] || DEVICE_PRESETS.desktop;
  const previewContainerWidth = previewPreset.containerWidth || previewPreset.width;
  const isMobilePreviewDevice = normalizedDevice === 'mobile';

  const computeFormHtml = useCallback(() => {
    if (!settings || !globalSettings) {
      return '<p>Start designing your email…</p>';
    }
    const html =
      generateEmailHtml(settings, globalSettings, { ignoreVisualEditor: true }) || '';
    const stripped = stripLeadingStyleTag(extractBody(html));
    return stripped || '<p>Start designing your email…</p>';
  }, [globalSettings, settings]);

  const buildInitialContent = useCallback(() => {
    const visual = resolvedVariantState.data || {};
    if (visual && typeof visual === 'object') {
      if (typeof visual.rawHtml === 'string' && visual.rawHtml.trim()) {
        const candidate = stripLeadingStyleTag(visual.rawHtml);
        if (!isPlainVisualHtml(candidate, settings)) {
          return candidate;
        }
      }
      if (typeof visual.html === 'string' && visual.html.trim()) {
        const candidate = stripLeadingStyleTag(extractBody(visual.html));
        if (!isPlainVisualHtml(candidate, settings)) {
          return candidate;
        }
      }
    }
    return computeFormHtml();
  }, [computeFormHtml, resolvedVariantState.data, settings]);

  const [content, setContent] = useState(() => {
    const initial = sanitizeEmailHtml(buildInitialContent() || '');
    contentRef.current = initial;
    return initial;
  });
  const editorValue = useMemo(
    () => wrapScriptletsWithPlaceholders(content),
    [content]
  );
  const suppressAutoSaveRef = useRef(true);
  useEffect(() => {
    suppressAutoSaveRef.current = true;
    const timer = setTimeout(() => {
      suppressAutoSaveRef.current = false;
    }, 1200);
    return () => clearTimeout(timer);
  }, [settings?.id]);

  useEffect(() => {
    hydratedVariantKeysRef.current.clear();
  }, [settings?.id]);

  const autoHydrateVariant = useCallback(
    layoutKey => {
      if (!layoutKey) return;
      const cache = hydratedVariantKeysRef.current;
      if (cache.has(layoutKey)) return;
      cache.add(layoutKey);
      const current = settings?.visualEditor;
      const nextState = removeVariantState(current, layoutKey);
      if (nextState !== current) {
        onUpdate('visualEditor', nextState);
      }
    },
    [onUpdate, settings?.visualEditor]
  );

  const updateHistoryState = useCallback(() => {
    const editor = joditRef.current;
    if (!editor?.history) {
      setHistoryState({ canUndo: false, canRedo: false });
      return;
    }
    try {
      setHistoryState({
        canUndo: editor.history.canUndo(),
        canRedo: editor.history.canRedo(),
      });
    } catch {
      setHistoryState({ canUndo: true, canRedo: true });
    }
  }, []);

  useEffect(() => {
    const previousKey = layoutVariantRef.current;
    if (previousKey === layoutVariant) {
      return;
    }
    const persist = persistVisualEditorRef.current;
    if (typeof persist === 'function' && previousKey) {
      try {
        persist(true, previousKey);
      } catch (error) {
        console.warn('Failed to persist visual editor before layout switch', error);
      }
    }

    layoutVariantRef.current = layoutVariant;

    const { key: variantKey, data: variant } = selectVariantState(
      settings?.visualEditor,
      layoutVariant
    );

    const fallbackHtml = computeFormHtml();
    let usedFallback = false;

    const deriveVariantHtml = () => {
      if (variant && typeof variant === 'object') {
        if (typeof variant.rawHtml === 'string' && variant.rawHtml.trim()) {
          const candidate = stripLeadingStyleTag(variant.rawHtml);
          if (!isPlainVisualHtml(candidate, settings)) {
            return candidate;
          }
        }
        if (typeof variant.html === 'string' && variant.html.trim()) {
          const candidate = stripLeadingStyleTag(extractBody(variant.html));
          if (!isPlainVisualHtml(candidate, settings)) {
            return candidate;
          }
        }
      }
      if (variant) {
        usedFallback = true;
      }
      return fallbackHtml;
    };

    const nextHtml = deriveVariantHtml();
    if (nextHtml === null || nextHtml === undefined) {
      setSaveStatus(prev => ({
        state: variant?.savedAt ? 'saved' : prev.state,
        savedAt: variant?.savedAt || prev.savedAt || null,
      }));
      if (variant?.savedAt) {
        setRelativeNow(Date.now());
      }
      return;
    }
    const sanitizedNext = sanitizeEmailHtml(nextHtml);
    if (sanitizedNext === contentRef.current) {
      setSaveStatus(prev => ({
        state: variant?.savedAt ? 'saved' : prev.state,
        savedAt: variant?.savedAt || prev.savedAt || null,
      }));
      if (variant?.savedAt) {
        setRelativeNow(Date.now());
      }
      return;
    }

    contentRef.current = sanitizedNext;
    setContent(sanitizedNext);

    if (editorReady && joditRef.current) {
      try {
        // Keep React state as the source of truth; avoid direct editor mutation
        // during lifecycle transitions.
      } catch (error) {
        console.warn('Failed to apply layout variant content to editor', error);
      }
      updateHistoryState();
    }

    if (variant?.savedAt) {
      setSaveStatus({ state: 'saved', savedAt: variant.savedAt });
      setRelativeNow(Date.now());
    } else {
      setSaveStatus(prev => ({ ...prev, state: 'idle', savedAt: null }));
    }

    if (usedFallback && variantKey) {
      autoHydrateVariant(variantKey);
    }
  }, [
    autoHydrateVariant,
    computeFormHtml,
    globalSettings,
    layoutVariant,
    settings,
    editorReady,
    updateHistoryState,
  ]);

  const previewDocument = useMemo(() => {
    const baseContent =
      content && content.trim() ? content : '<p>Start designing your email…</p>';
    const safeContent = sanitizeEmailHtml(baseContent);
    const baseStyles = `
      *, *::before, *::after { box-sizing: border-box; }
      body.preview-host {
        margin: 0;
        padding: ${isMobilePreviewDevice ? '12px 0 24px' : '12px 8px 24px'};
        background: ${isMobilePreviewDevice ? '#f4f6fb' : backgroundColor};
        display: flex;
        justify-content: center;
        min-height: 100vh;
      }
      body.preview-host--mobile {
        overflow-x: hidden;
      }
      .preview-host .preview-canvas {
        width: 100%;
        max-width: ${previewPreset.width}px;
      }
      ${
        isMobilePreviewDevice
          ? `.preview-host--mobile .preview-canvas {
        background: #ffffff;
        border-radius: 20px;
        margin: 20px;
        box-shadow: 0 20px 45px rgba(15,23,42,0.2);
        overflow: hidden;
        max-width: ${previewPreset.width}px;
      }
      .preview-host--mobile .preview-mobile-inner {
        width: 100%;
        overflow-x: hidden;
      }
      .preview-host--mobile .preview-mobile-inner table,
      .preview-host--mobile .preview-mobile-inner img,
      .preview-host--mobile .preview-mobile-inner svg {
        max-width: 100% !important;
      }
      .preview-host--mobile .preview-mobile-inner img,
      .preview-host--mobile .preview-mobile-inner svg {
        height: auto !important;
      }
      .preview-host--mobile .preview-mobile-inner table {
        margin-left: auto;
        margin-right: auto;
      }
      .preview-host--mobile .preview-mobile-inner td,
      .preview-host--mobile .preview-mobile-inner th {
        box-sizing: border-box;
        word-break: break-word;
      }`
          : ''
      }
      .preview-host .preview-canvas table { margin-left: auto; margin-right: auto; }
    `;
    const headCss = iframeStyles ? `<style>${iframeStyles}</style>` : '';
    const deviceClass = isMobilePreviewDevice ? 'preview-host preview-host--mobile' : 'preview-host';
    const canvasMarkup = isMobilePreviewDevice
      ? `<div class="preview-canvas"><div class="preview-mobile-inner">${safeContent}</div></div>`
      : `<div class="preview-canvas">${safeContent}</div>`;
    return `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8" /><meta name="viewport" content="width=device-width, initial-scale=1" />${headCss}<style>${baseStyles}</style></head><body class="${deviceClass}">${canvasMarkup}</body></html>`;
  }, [backgroundColor, content, iframeStyles, isMobilePreviewDevice, previewPreset.width]);

  useEffect(() => {
    ensureWorkspaceStyles();
  }, []);


  useEffect(() => {
    const handleFullscreenChange = () => {
      const active = Boolean(document.fullscreenElement);
      setIsFullscreen(active);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    const workspace = workspaceRef.current;
    if (!workspace) return;
    if (isFullscreen) {
      workspace.classList.add('kit-fullscreen-active');
    } else {
      workspace.classList.remove('kit-fullscreen-active');
    }
  }, [isFullscreen]);

  useEffect(() => {
    if (saveStatus.state !== 'saved') return undefined;
    const interval = setInterval(() => setRelativeNow(Date.now()), 15000);
    return () => clearInterval(interval);
  }, [saveStatus.state]);

  useEffect(() => {
    const savedAt = resolvedVariantState.data?.savedAt || null;
    setSaveStatus(prev => {
      if (savedAt && savedAt !== prev.savedAt) {
        return { state: 'saved', savedAt };
      }
      if (!savedAt && prev.savedAt !== null) {
        return { ...prev, savedAt: null };
      }
      return prev;
    });
  }, [resolvedVariantState.data?.savedAt]);

  useEffect(() => {
    setFontSizeDraft(formatState.fontSize || '');
  }, [formatState.fontSize]);

  useEffect(
    () => () => {
      if (autoSaveTimeout.current) {
        clearTimeout(autoSaveTimeout.current);
      }
    },
    []
  );

  useEffect(
    () => () => {
      const themeStyle = document.getElementById(DYNAMIC_THEME_STYLE_ID);
      if (themeStyle) {
        themeStyle.remove();
      }
    },
    []
  );

  useEffect(() => {
    const { key: variantKey, data: visual } = selectVariantState(
      settings?.visualEditor,
      layoutVariant
    );

    const fallbackHtml = computeFormHtml();
    let usedFallback = false;

    const deriveVisualHtml = () => {
      if (visual && typeof visual === 'object') {
        if (typeof visual.rawHtml === 'string' && visual.rawHtml.trim()) {
          const candidate = stripLeadingStyleTag(visual.rawHtml);
          if (!isPlainVisualHtml(candidate, settings)) {
            return candidate;
          }
        }
        if (typeof visual.html === 'string' && visual.html.trim()) {
          const candidate = stripLeadingStyleTag(extractBody(visual.html));
          if (!isPlainVisualHtml(candidate, settings)) {
            return candidate;
          }
        }
      }
      if (visual) {
        usedFallback = true;
      }
      return fallbackHtml;
    };

    const candidateHtml = deriveVisualHtml() || '<p>Start designing your email…</p>';
    const normalisedHtml = candidateHtml.trim() || fallbackHtml || '<p>Start designing your email…</p>';
    const signature = visual && typeof visual === 'object'
      ? visual.savedAt
        ? `saved:${visual.savedAt}`
        : `raw:${normalisedHtml}`
      : `fallback:${normalisedHtml}`;

    if (lastSyncedVisualRef.current === signature) {
      if (visual?.savedAt) {
        setSaveStatus(prev =>
          prev.savedAt === visual.savedAt ? prev : { state: 'saved', savedAt: visual.savedAt }
        );
        setRelativeNow(Date.now());
      }
      return;
    }

    lastSyncedVisualRef.current = signature;

    if (normalisedHtml === contentRef.current) {
      if (visual?.savedAt) {
        setSaveStatus({ state: 'saved', savedAt: visual.savedAt });
        setRelativeNow(Date.now());
      } else {
        setSaveStatus(prev => ({ ...prev, state: 'idle', savedAt: null }));
      }
      return;
    }

    contentRef.current = normalisedHtml;
    setContent(normalisedHtml);

    if (editorReady && joditRef.current) {
      try {
        // Keep React state as the source of truth; avoid direct editor mutation
        // during lifecycle transitions.
      } catch (error) {
        // ignore history reset issues
      }
      updateHistoryState();
    }

    if (visual?.savedAt) {
      setSaveStatus({ state: 'saved', savedAt: visual.savedAt });
      setRelativeNow(Date.now());
    } else {
      setSaveStatus(prev => ({ ...prev, state: 'idle', savedAt: null }));
    }

    if (usedFallback && variantKey) {
      autoHydrateVariant(variantKey);
    }
  }, [
    autoHydrateVariant,
    buildInitialContent,
    computeFormHtml,
    editorReady,
    layoutVariant,
    settings,
    settings?.visualEditor,
    updateHistoryState,
  ]);


  useEffect(() => {
    const bodyColor = settings?.bodyTextColor || '#1f2937';
    const backgroundColor = settings?.bodyBackgroundColor || '#f8fafc';
    const linkColor = settings?.linkColor || '#2563eb';
    const dividerColor = settings?.dividerColor || '#d1d5db';

    let styleEl = document.getElementById(DYNAMIC_THEME_STYLE_ID);
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = DYNAMIC_THEME_STYLE_ID;
      document.head.appendChild(styleEl);
    }

  styleEl.innerHTML = `
      .${WORKSPACE_CLASS} .kit-jodit-scope {
        font-family: ${fontStack};
        font-size: ${fontSizePt};
        line-height: ${lineHeightValue};
        color: ${bodyColor};
        background: ${backgroundColor};
      }
      .${WORKSPACE_CLASS} .kit-jodit-scope * {
        font-family: inherit;
        color: inherit;
      }
      .${WORKSPACE_CLASS} .kit-jodit-scope p,
      .${WORKSPACE_CLASS} .kit-jodit-scope li,
      .${WORKSPACE_CLASS} .kit-jodit-scope span,
      .${WORKSPACE_CLASS} .kit-jodit-scope td,
      .${WORKSPACE_CLASS} .kit-jodit-scope th,
      .${WORKSPACE_CLASS} .kit-jodit-scope div,
      .${WORKSPACE_CLASS} .kit-jodit-scope table,
      .${WORKSPACE_CLASS} .kit-jodit-scope ul,
      .${WORKSPACE_CLASS} .kit-jodit-scope ol {
        font-size: inherit;
        line-height: inherit;
      }
      .${WORKSPACE_CLASS} .kit-jodit-scope strong,
      .${WORKSPACE_CLASS} .kit-jodit-scope b {
        color: inherit;
      }
      .${WORKSPACE_CLASS} .kit-jodit-scope a {
        color: ${linkColor};
      }
      .${WORKSPACE_CLASS} .kit-jodit-scope hr {
        border: none;
        border-top: 1px solid ${dividerColor};
      }
      .${WORKSPACE_CLASS} .kit-ai-block-highlight {
        outline: 2px solid rgba(37, 99, 235, 0.45);
        background: rgba(37, 99, 235, 0.12);
        transition: outline 0.2s ease, background 0.2s ease;
      }
      .${WORKSPACE_CLASS} .kit-scriptlet-placeholder {
        display: inline-flex;
        align-items: center;
        padding: 0 6px;
        margin: 0 2px;
        border-radius: 6px;
        background: #fef3c7;
        color: #92400e;
        font-size: 11pt;
        font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace;
        border: 1px dashed rgba(146, 64, 14, 0.5);
        user-select: none;
        pointer-events: none;
      }
    `;
  }, [fontStack, fontSizePt, lineHeightValue, settings?.bodyTextColor, settings?.bodyBackgroundColor, settings?.linkColor, settings?.dividerColor]);

  const scheduleAutoSave = useCallback(() => {
    if (autoSaveTimeout.current) {
      clearTimeout(autoSaveTimeout.current);
    }
    setSaveStatus(prev => ({ ...prev, state: 'pending' }));
    autoSaveTimeout.current = setTimeout(() => {
      const persist = persistVisualEditorRef.current;
      if (typeof persist === 'function') {
        persist(true);
      }
      autoSaveTimeout.current = null;
    }, AUTO_SAVE_DELAY);
  }, []);

  const ensureFooterAnchors = root => {
    if (!root) {
      return { container: null, textNode: null };
    }
    let container = root.querySelector('[data-kit-footer="container"]');
    let textNode = root.querySelector('[data-kit-footer="text"]');
    if (container && textNode) {
      return { container, textNode };
    }

    const candidates = Array.from(root.querySelectorAll('td, th')).reverse();
    const fallbackContainer = candidates.find(node => {
      if (!node) return false;
      if (node.querySelector('[data-kit-footer="text"]')) return true;
      const text = (node.textContent || '').trim().toLowerCase();
      if (!text) return false;
      return text.includes('©') || text.includes('rights reserved');
    });

    if (!fallbackContainer) {
      return { container, textNode };
    }

    if (!container) {
      fallbackContainer.setAttribute('data-kit-footer', 'container');
      container = fallbackContainer;
    }

    textNode = container.querySelector('[data-kit-footer="text"]');
    if (!textNode) {
      const doc = container.ownerDocument || root.ownerDocument;
      const span = doc?.createElement ? doc.createElement('span') : null;
      if (span) {
        span.setAttribute('data-kit-footer', 'text');
        span.innerHTML = container.innerHTML;
        container.innerHTML = '';
        container.appendChild(span);
        textNode = span;
      }
    }

    return { container, textNode };
  };

  const applyBlockHighlight = useCallback(node => {
    if (aiBlockHighlightRef.current && aiBlockHighlightRef.current !== node) {
      aiBlockHighlightRef.current.classList.remove('kit-ai-block-highlight');
    }
    if (node) {
      node.classList.add('kit-ai-block-highlight');
      aiBlockHighlightRef.current = node;
    } else {
      aiBlockHighlightRef.current = null;
    }
  }, []);

  const collectAiBlocks = useCallback(() => {
    const editorInstance = joditRef.current;
    if (!editorInstance) {
      aiBlocksRef.current = new Map();
      setAiBlockOptions([]);
      return [];
    }
    const root = editorInstance.editor;
    if (!root) {
      aiBlocksRef.current = new Map();
      setAiBlockOptions([]);
      return [];
    }

    const blocks = [];
    const blockMap = new Map();

    const registerBlock = (node, label, meta = {}) => {
      if (!node) return;
      const path = buildDomPath(root, node);
      if (!path) return;
      const html = node.innerHTML;
      const text = htmlToPlainText(html || node.textContent || '').trim();
      if (!text) return;
      const idBase = meta.key || meta.type || 'block';
      const id = `${idBase}:${path.join('.')}`;
      if (blockMap.has(id)) return;
      const excerpt = text.length > 80 ? `${text.slice(0, 77)}…` : text;
      const descriptor = {
        id,
        label,
        excerpt,
        type: meta.type || 'block',
        path,
        sectionIndex: meta.sectionIndex,
        html,
        text,
      };
      blockMap.set(id, descriptor);
      blocks.push({ id, label, excerpt, type: descriptor.type, sectionIndex: meta.sectionIndex });
    };

    root.querySelectorAll('[data-kit-section-role="heading-text"]').forEach(node => {
      const sectionIndex = node.getAttribute('data-kit-section-index') || '';
      const headingText = htmlToPlainText(node.innerHTML || node.textContent || '').trim();
      const label = headingText
        ? `Heading: ${headingText}`
        : sectionIndex
        ? `Heading ${Number(sectionIndex) + 1}`
        : 'Heading';
      registerBlock(node, label, {
        type: 'section-heading',
        sectionIndex,
        key: `heading-${sectionIndex}`,
      });
    });

    root.querySelectorAll('[data-kit-section-role="body-text"]').forEach(node => {
      const sectionIndex = node.getAttribute('data-kit-section-index') || '';
      const bodyText = htmlToPlainText(node.innerHTML || node.textContent || '').trim();
      if (!bodyText) return;
      const label = sectionIndex
        ? `Section ${Number(sectionIndex) + 1} body`
        : 'Section body';
      registerBlock(node, label, {
        type: 'section-body',
        sectionIndex,
        key: `body-${sectionIndex}`,
      });
    });

    root.querySelectorAll('[data-kit-footer="container"]').forEach((node, index) => {
      registerBlock(node, 'Footer', { type: 'footer', key: `footer-${index}` });
    });

    root.querySelectorAll('[data-cta-block="true"]').forEach((node, index) => {
      registerBlock(node, `CTA block ${index + 1}`, { type: 'cta', key: `cta-${index}` });
    });

    const calendarWrappers = root.querySelectorAll('[data-kit-calendar-wrapper="true"]');
    if (calendarWrappers.length > 0) {
      calendarWrappers.forEach((node, index) => {
        registerBlock(node, `Calendar buttons${index ? ` ${index + 1}` : ''}`, {
          type: 'calendar-button',
          key: `calendar-${index}`,
        });
      });
    } else {
      root.querySelectorAll('[data-kit-calendar-button]').forEach((node, index) => {
        registerBlock(node, `Calendar button${index ? ` ${index + 1}` : ''}`, {
          type: 'calendar-button',
          key: `calendar-${index}`,
        });
      });
    }

    root.querySelectorAll('td').forEach((node, index) => {
      if (!node) return;
      if (node.querySelector('[data-kit-footer="text"]')) return;
      const cellText = htmlToPlainText(node.innerHTML || '').trim();
      if (!cellText) return;
      const label = `Column cell ${index + 1}`;
      registerBlock(node, label, { type: 'table-cell', key: `cell-${index}` });
    });

    aiBlocksRef.current = blockMap;
    setAiBlockOptions(blocks);
    return blocks;
  }, []);

  const resolveBlockNode = useCallback(id => {
    const editorInstance = joditRef.current;
    if (!editorInstance) return null;
    const root = editorInstance.editor;
    if (!root) return null;
    const entry = aiBlocksRef.current.get(id);
    if (!entry) return null;
    return resolveDomPath(root, entry.path);
  }, []);

  const findBlockIdForNode = useCallback(
    node => {
      if (!node) return '';
      const editorInstance = joditRef.current;
      if (!editorInstance) return '';
      const root = editorInstance.editor;
      if (!root) return '';
      const element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
      if (!element) return '';
      for (const [id, entry] of aiBlocksRef.current.entries()) {
        const blockNode = resolveDomPath(root, entry.path);
        if (blockNode && blockNode.contains(element)) {
          return id;
        }
      }
      return '';
    },
    []
  );
  const applyFooterTextUpdate = useCallback(nextSettings => {
    const editorInstance = joditRef.current;
    if (!editorInstance) return false;
    const root = editorInstance.editor;
    if (!root) return false;
    const { textNode: target } = ensureFooterAnchors(root);
    if (!target) return false;
    const nextHtml = buildStyledTextHtml(
      nextSettings?.footerText ?? '',
      nextSettings?.footerStyles || []
    );
    const normalizedNext = (nextHtml || '').trim() || '&nbsp;';
    const currentHtml = (target.innerHTML || '').trim();
    if (currentHtml === normalizedNext) {
      return false;
    }
    target.innerHTML = normalizedNext;
    return true;
  }, []);

  const applyFooterStyleUpdate = useCallback(nextSettings => {
    const editorInstance = joditRef.current;
    if (!editorInstance) return false;
    const root = editorInstance.editor;
    if (!root) return false;
    const { container } = ensureFooterAnchors(root);
    if (!container) return false;
    const fontFamily = getFontStack(
      nextSettings?.templateFontFamily || DEFAULT_FONT_FAMILY
    );
    let changed = false;
    const assign = (prop, value) => {
      if (container.style[prop] !== value) {
        container.style[prop] = value;
        changed = true;
      }
    };
    assign('backgroundColor', nextSettings?.footerBackgroundColor || '');
    assign('color', nextSettings?.footerTextColor || '');
    assign('fontSize', `${nextSettings?.footerFontSize || 12}pt`);
    assign('fontFamily', fontFamily);
    assign('padding', `${nextSettings?.footerPadding || 0}px`);
    assign('textAlign', 'center');
    assign('borderBottomLeftRadius', `${nextSettings?.footerBorderRadius || 0}px`);
    assign('borderBottomRightRadius', `${nextSettings?.footerBorderRadius || 0}px`);
    return changed;
  }, []);

  const persistVisualEditor = useCallback(
    (silent = true, layoutKeyOverride = null) => {
      setSaveStatus(prev => ({ ...prev, state: 'saving' }));
      const highlightedNode = aiBlockHighlightRef.current;
      if (highlightedNode) {
        highlightedNode.classList.remove('kit-ai-block-highlight');
      }

      const html = contentRef.current || '';
      const inlineHtml = iframeStyles ? `<style>${iframeStyles}</style>${html}` : html;

      const variantKey = layoutKeyOverride || layoutVariant;
      const currentVariant = selectVariantState(
        settings?.visualEditor,
        variantKey
      ).data;
      const nextVersion = (currentVariant?.version || 0) + 1;

      const payload = {
        projectData: null,
        html: inlineHtml,
        rawHtml: html,
        css: iframeStyles,
        savedAt: Date.now(),
        version: nextVersion,
      };

      const nextState = upsertVariantState(
        settings?.visualEditor,
        variantKey,
        payload
      );

      onUpdate('visualEditor', nextState);
      setSaveStatus({ state: 'saved', savedAt: payload.savedAt });
      setRelativeNow(Date.now());
      if (!silent) {
        showNotification('Visual changes saved.', 'success');
      }
      if (highlightedNode) {
        highlightedNode.classList.add('kit-ai-block-highlight');
      }
      return nextState;
    },
    [
      iframeStyles,
      layoutVariant,
      onUpdate,
      settings?.visualEditor,
      showNotification,
    ]
  );

  useEffect(() => {
    persistVisualEditorRef.current = persistVisualEditor;
  }, [persistVisualEditor]);

  useEffect(() => {
    if (typeof onRegisterPersist === 'function') {
      onRegisterPersist(persistVisualEditor);
      return () => onRegisterPersist(null);
    }
    return undefined;
  }, [onRegisterPersist, persistVisualEditor]);

  const syncCtaButtonsWithDom = useCallback(() => {
    const editor = joditRef.current;
    if (!editor) return;
    const root = editor.editor;
    if (!root) return;

    const anchors = Array.from(root.querySelectorAll('a[data-cta-id]'));
    const domIds = anchors
      .map(anchor => anchor.getAttribute('data-cta-id'))
      .filter(Boolean);
    const uniqueDomIds = Array.from(new Set(domIds));

    const currentButtons = Array.isArray(settings?.ctaButtons)
      ? settings.ctaButtons
      : [];

    const preservedButtons = currentButtons.filter(button => uniqueDomIds.includes(button.id));
    const missingIds = uniqueDomIds.filter(
      id => !currentButtons.some(button => button.id === id)
    );

    if (missingIds.length === 0 && preservedButtons.length === currentButtons.length) {
      return;
    }

    const nextButtons = [...preservedButtons];

    missingIds.forEach(id => {
      const anchor = anchors.find(node => node.getAttribute('data-cta-id') === id);
      nextButtons.push({
        id,
        text: anchor?.textContent?.trim() || 'Call to action',
        url: anchor?.getAttribute('href') || 'https://example.com',
        backgroundColor: settings?.linkColor || '#2563eb',
        textColor: '#ffffff',
        fontSize: Math.max(14, Math.round((fontSizeNumber || 16) + 2)),
        paddingX: 24,
        paddingY: 12,
        borderRadius: 999,
        placement: 'after-intro',
      });
    });

    onUpdate('ctaButtons', nextButtons);
  }, [fontSizeNumber, onUpdate, settings?.ctaButtons, settings?.linkColor]);

  const handleContentChange = useCallback(
    value => {
      const editorValue = typeof value === 'string' ? value : joditRef.current?.value || '';
      const restoredValue = unwrapScriptletPlaceholders(editorValue);
      const sanitizedValue = sanitizeEmailHtml(restoredValue);
      if (sanitizedValue === contentRef.current) {
        updateHistoryState();
        return;
      }
      contentRef.current = sanitizedValue;
      setContent(sanitizedValue);
      if (!suppressAutoSaveRef.current) {
        scheduleAutoSave();
      }
      updateHistoryState();
      syncCtaButtonsWithDom();
    },
    [scheduleAutoSave, updateHistoryState, syncCtaButtonsWithDom]
  );

  const insertHtml = useCallback(
    html => {
      const editor = joditRef.current;
      if (!editor) return;
      editor.focus();
      editor.s.insertHTML(html);
      handleContentChange(editor.value);
    },
    [handleContentChange]
  );

  const handleInsertBlock = useCallback(
    block => {
      if (!block) return;
      if (block.html) {
        insertHtml(block.html);
      }
    },
    [insertHtml]
  );

  useEffect(() => {
    const previous = prevSettingsRef.current;
    prevSettingsRef.current = settings;

    if (!editorReady) return;
    if (!settings?.visualEditor) return;
    if (!previous) return;

    const editorInstance = joditRef.current;
    const root = editorInstance?.editor;
    const doc = editorInstance?.editorDocument || editorInstance?.ownerDocument;
    const win = editorInstance?.editorWindow || doc?.defaultView || window;

    const normalizeColorString = value => (value || '').trim().toLowerCase();
    const canonicalColor = value => {
      if (!doc || !win || !value) return null;
      const temp = doc.createElement('span');
      temp.style.color = value;
      doc.body.appendChild(temp);
      const computed = win.getComputedStyle(temp).color;
      temp.remove();
      return computed;
    };

    if (editorInstance && root && doc) {
      if (previous.bodyBackgroundColor !== settings.bodyBackgroundColor) {
        root.style.backgroundColor = settings.bodyBackgroundColor || '#ffffff';
        const mainTable =
          root.querySelector('[data-kit-main-table]') ||
          root.querySelector('table[role="presentation"]');
        if (mainTable) {
          mainTable.style.backgroundColor = settings.bodyBackgroundColor || '#ffffff';
        }
      }

      if (previous.bodyTextColor !== settings.bodyTextColor) {
        const prevColorCanonical = canonicalColor(previous.bodyTextColor);
        const nextColor = settings.bodyTextColor;
        const nodes = Array.from(root.querySelectorAll('[style]'));
        nodes.forEach(node => {
          const inlineColor = node.style?.color;
          if (!inlineColor) return;
          const normalizedInline = normalizeColorString(inlineColor);
          if (normalizedInline && normalizedInline === normalizeColorString(previous.bodyTextColor)) {
            node.style.color = nextColor;
            return;
          }
          if (prevColorCanonical && canonicalColor(inlineColor) === prevColorCanonical) {
            node.style.color = nextColor;
          }
        });
        root.style.color = nextColor;
      }

      if (previous.linkColor !== settings.linkColor) {
        const prevLinkCanonical = canonicalColor(previous.linkColor);
        const anchors = Array.from(root.querySelectorAll('a'));
        anchors.forEach(anchor => {
          const inlineColor = anchor.style?.color;
          if (!inlineColor) return;
          const normalizedInline = normalizeColorString(inlineColor);
          if (normalizedInline && normalizedInline === normalizeColorString(previous.linkColor)) {
            anchor.style.color = settings.linkColor;
            return;
          }
          if (prevLinkCanonical && canonicalColor(inlineColor) === prevLinkCanonical) {
            anchor.style.color = settings.linkColor;
          }
        });
      }

      if (previous.dividerColor !== settings.dividerColor) {
        const prevDividerCanonical = canonicalColor(previous.dividerColor);
        const matchesPreviousColor = value => {
          if (!value) return false;
          const normalized = normalizeColorString(value);
          if (normalized && normalized === normalizeColorString(previous.dividerColor)) {
            return true;
          }
          return prevDividerCanonical && canonicalColor(value) === prevDividerCanonical;
        };

        const elements = Array.from(root.querySelectorAll('[style], [data-divider-line]'));
        elements.forEach(node => {
          if (node.hasAttribute('data-divider-line')) {
            node.style.backgroundColor = settings.dividerColor;
            node.style.borderColor = settings.dividerColor;
            return;
          }
          const borderTopColor = node.style?.borderTopColor;
          if (borderTopColor && matchesPreviousColor(borderTopColor)) {
            node.style.borderTopColor = settings.dividerColor;
            return;
          }
          if (node.style?.borderColor && matchesPreviousColor(node.style.borderColor)) {
            node.style.borderColor = settings.dividerColor;
          }
          if (node.style?.backgroundColor && matchesPreviousColor(node.style.backgroundColor)) {
            node.style.backgroundColor = settings.dividerColor;
            return;
          }
          if (node.style?.background && matchesPreviousColor(node.style.background)) {
            node.style.background = settings.dividerColor;
          }
        });
      }

      const parseLineHeightValue = value => {
        if (value === undefined || value === null || value === '') return null;
        const parsed = parseFloat(value);
        return Number.isNaN(parsed) ? null : parsed;
      };
      const prevLineHeight = parseLineHeightValue(previous.paragraphLineHeight) ?? 1.6;
      const nextLineHeight = parseLineHeightValue(settings.paragraphLineHeight) ?? 1.6;
      if (Math.abs(prevLineHeight - nextLineHeight) > 0.001) {
        root.style.lineHeight = nextLineHeight;
        const nodes = Array.from(root.querySelectorAll('[style*="line-height"]'));
        nodes.forEach(node => {
          const inlineValue = parseLineHeightValue(node.style?.lineHeight);
          if (inlineValue !== null && Math.abs(inlineValue - prevLineHeight) < 0.001) {
            node.style.lineHeight = nextLineHeight;
          }
        });
      }
    }

    const footerTextChanged =
      previous.footerText !== settings.footerText ||
      JSON.stringify(previous.footerStyles || []) !==
        JSON.stringify(settings.footerStyles || []);

    const footerStyleChanged =
      previous.footerBackgroundColor !== settings.footerBackgroundColor ||
      previous.footerTextColor !== settings.footerTextColor ||
      previous.footerFontSize !== settings.footerFontSize ||
      previous.footerPadding !== settings.footerPadding ||
      previous.footerBorderRadius !== settings.footerBorderRadius ||
      previous.templateFontFamily !== settings.templateFontFamily;

    if (!footerTextChanged && !footerStyleChanged) {
      return;
    }

    let mutated = false;
    if (footerTextChanged) {
      mutated = applyFooterTextUpdate(settings) || mutated;
    }
    if (footerStyleChanged) {
      mutated = applyFooterStyleUpdate(settings) || mutated;
    }

    if (mutated) {
      if (editorInstance) {
        const nextHtml = editorInstance.value;
        contentRef.current = nextHtml;
        setContent(nextHtml);
        scheduleAutoSave();
      }
    }
  }, [
    applyFooterStyleUpdate,
    applyFooterTextUpdate,
    editorReady,
    scheduleAutoSave,
    setContent,
    settings,
  ]);

  useEffect(() => {
    if (!isAiPanelOpen) return;
    collectAiBlocks();
  }, [collectAiBlocks, content, isAiPanelOpen]);

  const updateFormatState = useCallback(() => {
    const editor = joditRef.current;
    if (!editor) return;
    const doc = editor.editorDocument || editor.ownerDocument;
    const root = editor.editor;
    if (!doc || !root) return;

    const queryState = command => {
      try {
        return doc.queryCommandState(command);
      } catch (error) {
        return false;
      }
    };

    const selection = editor.s.sel;
    const focusNode = selection?.focusNode || selection?.anchorNode || root;
    const anchorElement = focusNode
      ? findAncestorElement(focusNode, node => node.nodeName === 'A', root)
      : null;

    let blockTag = 'p';
    let walker = focusNode;
    while (walker && walker !== root) {
      if (walker.nodeType === 1) {
        const tag = walker.nodeName.toLowerCase();
        if (SUPPORTED_BLOCK_TAGS.includes(tag)) {
          blockTag = tag;
          break;
        }
      }
      walker = walker.parentNode;
    }

    const resolveFontElement = () => {
      if (anchorElement) {
        return anchorElement;
      }
      if (!focusNode) {
        return root;
      }
      if (focusNode.nodeType === Node.ELEMENT_NODE) {
        return focusNode;
      }
      if (focusNode.parentElement) {
        return focusNode.parentElement;
      }
      return root;
    };

    let fontSizeValue = '';
    const fontElement = resolveFontElement();
    const computeFrom = fontElement && fontElement.nodeType === Node.ELEMENT_NODE ? fontElement : null;
    const win = editor.editorWindow || window;
    if (computeFrom && typeof win?.getComputedStyle === 'function') {
      try {
        const computed = win.getComputedStyle(computeFrom);
        if (computed?.fontSize) {
          const numeric = parseFloat(computed.fontSize);
          if (Number.isFinite(numeric) && numeric > 0) {
            const ptValue = numeric * 0.75;
            fontSizeValue = String(Math.round(ptValue));
          }
        }
      } catch (error) {
        // ignore style lookup failures
      }
    }

    const nextState = {
      bold: Boolean(queryState('bold')),
      italic: Boolean(queryState('italic')),
      underline: Boolean(queryState('underline')),
      strike: Boolean(queryState('strikeThrough')),
      orderedList: Boolean(queryState('insertOrderedList')),
      unorderedList: Boolean(queryState('insertUnorderedList')),
      alignment: queryState('justifyFull')
        ? 'justify'
        : queryState('justifyRight')
        ? 'right'
        : queryState('justifyCenter')
        ? 'center'
        : 'left',
      blockTag,
      linkActive: Boolean(anchorElement),
      fontSize: fontSizeValue,
    };

    setFormatState(prev => {
      if (
        prev.bold === nextState.bold &&
        prev.italic === nextState.italic &&
        prev.underline === nextState.underline &&
        prev.strike === nextState.strike &&
        prev.orderedList === nextState.orderedList &&
        prev.unorderedList === nextState.unorderedList &&
        prev.alignment === nextState.alignment &&
        prev.blockTag === nextState.blockTag &&
        prev.linkActive === nextState.linkActive &&
        prev.fontSize === nextState.fontSize
      ) {
        return prev;
      }
      return nextState;
    });
  }, []);

  useEffect(() => {
    if (!editorReady) return;
    updateFormatState();
  }, [editorReady, updateFormatState]);

  const runEditorCommand = useCallback(
    (command, value = null) => {
      const editor = joditRef.current;
      if (!editor) return;
      editor.focus();
      try {
        if (value === null) {
          editor.execCommand(command, false, null);
        } else {
          editor.execCommand(command, false, value);
        }
      } catch (error) {
        /* swallow command errors */
      }
      handleContentChange(editor.value);
      updateFormatState();
    },
    [handleContentChange, updateFormatState]
  );

  const handleToggleList = useCallback(
    type => {
      if (type === 'ordered') {
        runEditorCommand('insertOrderedList');
      } else {
        runEditorCommand('insertUnorderedList');
      }
    },
    [runEditorCommand]
  );

  const applyFontSizeValue = useCallback(
    value => {
      const numeric = Number.parseFloat(value);
      if (!Number.isFinite(numeric) || numeric <= 0) {
        return;
      }
      const clamped = Math.min(96, Math.max(8, Math.round(numeric)));
      runEditorCommand('fontsize', `${clamped}pt`);
      setFontSizeDraft(String(clamped));
    },
    [runEditorCommand]
  );

  const handleFontSizeStep = useCallback(
    delta => {
      const currentValue = Number.parseFloat(fontSizeDraft || formatState.fontSize || `${fontSizeNumber}`);
      const fallback = Number.isFinite(currentValue) && currentValue > 0 ? currentValue : fontSizeNumber;
      const next = fallback + delta;
      applyFontSizeValue(next);
    },
    [applyFontSizeValue, fontSizeDraft, formatState.fontSize, fontSizeNumber]
  );

  const handleFontSizeInputChange = useCallback(event => {
    setFontSizeDraft(event.target.value);
  }, []);

  const handleFontSizeReset = useCallback(() => {
    const editor = joditRef.current;
    if (!editor) return;
    editor.focus();
    const root = editor.editor;
    const cleanup = node => {
      if (!node) return;
      const baseElement =
        node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
      if (!baseElement) return;
      const target = findAncestorElement(
        baseElement,
        element => element?.style && element.style.fontSize,
        root
      );
      if (target) {
        target.style.fontSize = '';
        if (typeof target.getAttribute === 'function' && !target.getAttribute('style')) {
          target.removeAttribute('style');
        }
      }
    };

    if (typeof editor.s.isCollapsed === 'function' && editor.s.isCollapsed()) {
      cleanup(editor.s.current());
    } else {
      editor.s.eachSelection(cleanup);
    }

    handleContentChange(editor.value);
    updateFormatState();
    setFontSizeDraft('');
  }, [handleContentChange, updateFormatState]);

  const commitFontSizeDraft = useCallback(() => {
    if (!fontSizeDraft) {
      handleFontSizeReset();
      return;
    }
    const numeric = Number.parseFloat(fontSizeDraft);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      setFontSizeDraft(formatState.fontSize || '');
      return;
    }
    applyFontSizeValue(numeric);
  }, [applyFontSizeValue, fontSizeDraft, formatState.fontSize, handleFontSizeReset]);

  const handleAlignChange = useCallback(
    alignment => {
      const commandMap = {
        left: 'justifyLeft',
        center: 'justifyCenter',
        right: 'justifyRight',
        justify: 'justifyFull',
      };
      const command = commandMap[alignment];
      if (!command) return;
      runEditorCommand(command);
    },
    [runEditorCommand]
  );

  const handleClearFormatting = useCallback(() => {
    runEditorCommand('removeFormat');
  }, [runEditorCommand]);

  const handleRemoveLink = useCallback(() => {
    runEditorCommand('unlink');
  }, [runEditorCommand]);

  const resetLinkToolState = useCallback(() => {
    setLinkToolState({ isOpen: false, mode: 'web', displayText: '', address: '' });
    setLinkToolError('');
  }, []);

  const closeLinkTool = useCallback(() => {
    const editor = joditRef.current;
    if (editor && linkSelectionRef.current) {
      try {
        editor.s.restore();
      } catch (error) {
        /* noop */
      }
      editor.s.removeMarkers();
    }
    linkSelectionRef.current = null;
    resetLinkToolState();
    updateFormatState();
  }, [resetLinkToolState, updateFormatState]);

  const openLinkTool = useCallback(() => {
    const editor = joditRef.current;
    if (!editor) return;
    editor.focus();
    linkSelectionRef.current = editor.s.save();
    const selectionHtml = editor.s.html || '';
    const selectionText = htmlToPlainText(selectionHtml);
    const selection = editor.s.sel;
    const root = editor.editor;
    const focusNode = selection?.focusNode || selection?.anchorNode || root;
    const activeAnchor = focusNode
      ? findAncestorElement(focusNode, node => node.nodeName === 'A', root)
      : null;

    let inferredMode = 'web';
    let inferredAddress = '';
    let inferredText = selectionText || '';

    if (activeAnchor) {
      const parsed = parseHrefForLink(activeAnchor.getAttribute('href') || '');
      inferredMode = parsed.type;
      inferredAddress = parsed.value || '';
      inferredText = activeAnchor.textContent || inferredText;
    }

    if (!inferredText) {
      inferredText = inferredAddress || inferredText;
    }

    if (toolbarRef.current && linkButtonRef.current) {
      const toolbarRect = toolbarRef.current.getBoundingClientRect();
      const buttonRect = linkButtonRef.current.getBoundingClientRect();
      setLinkPopoverCoords({
        top: buttonRect.bottom - toolbarRect.top + 8,
        left: buttonRect.left - toolbarRect.left,
      });
    } else {
      setLinkPopoverCoords({ top: 56, left: 0 });
    }

    setLinkToolState({
      isOpen: true,
      mode: inferredMode,
      displayText: inferredText,
      address: inferredAddress,
    });
    setLinkToolError('');
    updateFormatState();
  }, [updateFormatState]);

  const applyLink = useCallback(() => {
    if (!linkToolState.isOpen) return;
    const editor = joditRef.current;
    if (!editor) return;

    const href = buildHrefForLinkType(linkToolState.mode, linkToolState.address);
    if (!href) {
      setLinkToolError('Please provide a valid value.');
      return;
    }

    setLinkToolError('');

    editor.focus();
    if (linkSelectionRef.current) {
      try {
        editor.s.restore();
      } catch (error) {
        /* noop */
      }
      editor.s.removeMarkers();
      linkSelectionRef.current = null;
    }

    const selection = editor.s.sel;
    const root = editor.editor;
    const focusNode = selection?.focusNode || selection?.anchorNode || root;
    let activeAnchor = focusNode
      ? findAncestorElement(focusNode, node => node.nodeName === 'A', root)
      : null;
    const label = (linkToolState.displayText || '').trim();
    const shouldOpenInNewTab = linkToolState.mode === 'web' || linkToolState.mode === 'location';

    if (activeAnchor) {
      activeAnchor.setAttribute('href', href);
      if (label) {
        activeAnchor.textContent = label;
      }
      if (shouldOpenInNewTab) {
        activeAnchor.setAttribute('target', '_blank');
        activeAnchor.setAttribute('rel', 'noopener noreferrer');
      } else {
        activeAnchor.removeAttribute('target');
        activeAnchor.removeAttribute('rel');
      }
    } else if (editor.s.isCollapsed()) {
      const text = label || linkToolState.address.trim();
      const html = `<a href="${escapeAttribute(href)}"${
        shouldOpenInNewTab ? ' target="_blank" rel="noopener noreferrer"' : ''
      }>${escapeHtml(text)}</a>`;
      editor.s.insertHTML(html);
    } else {
      try {
        editor.execCommand('createLink', false, href);
      } catch (error) {
        /* noop */
      }
      const freshSelection = editor.s.sel;
      const freshFocus = freshSelection?.focusNode || freshSelection?.anchorNode;
      activeAnchor = freshFocus
        ? findAncestorElement(freshFocus, node => node.nodeName === 'A', root)
        : null;
      if (activeAnchor) {
        if (label) {
          activeAnchor.textContent = label;
        }
        if (shouldOpenInNewTab) {
          activeAnchor.setAttribute('target', '_blank');
          activeAnchor.setAttribute('rel', 'noopener noreferrer');
        } else {
          activeAnchor.removeAttribute('target');
          activeAnchor.removeAttribute('rel');
        }
      }
    }

    handleContentChange(editor.value);
    resetLinkToolState();
    updateFormatState();
  }, [handleContentChange, linkToolState, resetLinkToolState, updateFormatState]);

  const handleLinkFieldChange = useCallback((field, value) => {
    setLinkToolState(prev => ({ ...prev, [field]: value }));
  }, []);

  const captureCaretBookmark = useCallback(() => {
    const editor = joditRef.current;
    if (!editor) {
      aiCaretRef.current = null;
      return;
    }
    const selection = editor.s.sel;
    if (!selection || selection.rangeCount === 0) {
      aiCaretRef.current = null;
      return;
    }
    const range = selection.getRangeAt(0);
    const root = editor.editor;
    if (!root) {
      aiCaretRef.current = null;
      return;
    }
    const path = buildDomPath(root, range.startContainer);
    if (!path) {
      aiCaretRef.current = null;
      return;
    }
    aiCaretRef.current = {
      path,
      offset: range.startOffset,
      collapsed: range.collapsed !== false,
    };
  }, []);

  useEffect(() => {
    if (!linkToolState.isOpen) return undefined;
    const handlePointer = event => {
      if (
        linkPopoverRef.current?.contains(event.target) ||
        linkButtonRef.current?.contains(event.target)
      ) {
        return;
      }
      closeLinkTool();
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('touchstart', handlePointer);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('touchstart', handlePointer);
    };
  }, [linkToolState.isOpen, closeLinkTool]);

  useEffect(() => {
    if (!linkToolState.isOpen) return undefined;
    const handleKeydown = event => {
      if (event.key === 'Escape') {
        closeLinkTool();
      }
    };
    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [linkToolState.isOpen, closeLinkTool]);

  useEffect(() => {
    if (viewMode === 'preview' && linkToolState.isOpen) {
      closeLinkTool();
    }
  }, [viewMode, linkToolState.isOpen, closeLinkTool]);


  const handleOpenAiPanel = useCallback(() => {
    const editor = joditRef.current;
    if (!editor) return;
    editor.focus();
    aiSelectionRef.current = editor.s.save();
    captureCaretBookmark();
    const selectionHtml = editor.s.html || '';
    const selectionText = htmlToPlainText(selectionHtml);
    const rangeInfo = captureSelectionRangeInfo(editor);
    const match = matchSelectionToSetting(selectionHtml || selectionText, settings);
    const blocks = collectAiBlocks();
    let matchedBlockId = '';
    if (blocks.length) {
      const anchorNode = editor.s.sel?.anchorNode;
      matchedBlockId = findBlockIdForNode(anchorNode);
    }
    setAiSelectedBlockId(matchedBlockId || '');
    if (matchedBlockId) {
      const blockNode = resolveBlockNode(matchedBlockId);
      applyBlockHighlight(blockNode);
    } else {
      applyBlockHighlight(null);
    }
    setAiSelectionSummary(prev => ({
      ...prev,
      html: selectionHtml,
      text: selectionText || htmlToPlainText(editor.value || ''),
      sectionId: match?.id || null,
      fieldPath: match,
      scope: 'selection',
      blockId: matchedBlockId || null,
      rangeInfo: rangeInfo || null,
    }));
    setAiPrompt('');
    setAiPreview('');
    setAiStatus({ state: 'idle', message: '' });
    setAiRestrictToSelection(Boolean((selectionText || '').trim()));
    setAiPanelOpen(true);
  }, [applyBlockHighlight, collectAiBlocks, findBlockIdForNode, resolveBlockNode, captureCaretBookmark, settings]);

  const resetAiPanelState = useCallback(() => {
    setAiPanelOpen(false);
    setAiPreview('');
    setAiStatus({ state: 'idle', message: '' });
    setAiProgress(0);
    applyBlockHighlight(null);
    setAiSelectedBlockId('');
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  }, [applyBlockHighlight]);

  const handleCloseAiPanel = useCallback(() => {
    const editor = joditRef.current;
    if (editor && aiSelectionRef.current) {
      try {
        editor.s.removeMarkers();
      } catch (error) {
        /* ignore marker cleanup issues */
      }
    }
    aiSelectionRef.current = null;
    aiCaretRef.current = null;
    applyBlockHighlight(null);
    setAiSelectedBlockId('');
    resetAiPanelState();
  }, [applyBlockHighlight, resetAiPanelState]);

  const handleAiCaptureSelection = useCallback(() => {
    const editor = joditRef.current;
    if (!editor) return;
    editor.focus();
    aiSelectionRef.current = editor.s.save();
    captureCaretBookmark();
    const selectionHtml = editor.s.html || '';
    const selectionText = htmlToPlainText(selectionHtml);
    const rangeInfo = captureSelectionRangeInfo(editor);
    const match = matchSelectionToSetting(selectionHtml || selectionText, settings);
    const blocks = collectAiBlocks();
    let matchedBlockId = '';
    if (blocks.length) {
      const anchorNode = editor.s.sel?.anchorNode;
      matchedBlockId = findBlockIdForNode(anchorNode);
    }
    setAiSelectedBlockId(matchedBlockId || '');
    if (matchedBlockId) {
      const node = resolveBlockNode(matchedBlockId);
      applyBlockHighlight(node);
    } else {
      applyBlockHighlight(null);
    }
    setAiSelectionSummary(prev => ({
      ...prev,
      html: selectionHtml,
      text: selectionText || htmlToPlainText(editor.value || ''),
      sectionId: match?.id || null,
      fieldPath: match,
      scope: 'selection',
      blockId: matchedBlockId || null,
      rangeInfo: rangeInfo || null,
    }));
    setAiRestrictToSelection(Boolean((selectionText || '').trim()));
  }, [
    applyBlockHighlight,
    collectAiBlocks,
    findBlockIdForNode,
    resolveBlockNode,
    captureCaretBookmark,
    settings,
  ]);

  const handleAiSelectBlock = useCallback(
    id => {
      const nextId = typeof id === 'string' ? id : '';
      setAiSelectedBlockId(nextId);
      if (!nextId) {
        applyBlockHighlight(null);
        return;
      }
      const entry = aiBlocksRef.current.get(nextId);
      const node = resolveBlockNode(nextId);
      if (node) {
        applyBlockHighlight(node);
      } else {
        applyBlockHighlight(null);
      }
      if (entry) {
        const html = node?.innerHTML || entry.html || '';
        const text = htmlToPlainText(html);
        const match = matchSelectionToSetting(html || text, settings);
        setAiSelectionSummary(prev => ({
          ...prev,
          html,
          text,
          sectionId: match?.id || null,
          fieldPath: match,
          scope: 'selection',
          blockId: nextId,
          rangeInfo: null,
        }));
        setAiRestrictToSelection(true);
      }
    },
    [applyBlockHighlight, resolveBlockNode, settings]
  );

  const restoreSelectionIfNeeded = useCallback(editor => {
    if (!editor || !aiSelectionRef.current) return;
    let restored = false;
    try {
      restored = editor.s.restore(aiSelectionRef.current) !== false;
    } catch (error) {
      /* restoring can fail if DOM changed; ignore */
    }
    if (!restored) {
      try {
        editor.s.restore();
        restored = true;
      } catch (error) {
        /* ignore fallback restore issues */
      }
    }
    if (!restored && aiCaretRef.current) {
      const { path, offset, collapsed } = aiCaretRef.current;
      const root = editor.editor;
      const doc = editor.editorDocument || editor.ownerDocument || document;
      const target = resolveDomPath(root, path);
      if (target && doc) {
        try {
          const range = doc.createRange();
          if (target.nodeType === Node.TEXT_NODE) {
            const textLength = target.textContent ? target.textContent.length : 0;
            const safeOffset = Math.min(Math.max(offset, 0), textLength);
            range.setStart(target, safeOffset);
          } else {
            const childCount = target.childNodes ? target.childNodes.length : 0;
            const safeOffset = Math.min(Math.max(offset, 0), childCount);
            range.setStart(target, safeOffset);
          }
          range.collapse(collapsed);
          editor.s.selectRange(range);
          restored = true;
        } catch (error) {
          /* unable to restore path */
        }
      }
    }
    if (restored) {
      try {
        editor.s.removeMarkers();
      } catch (error) {
        /* ignore marker cleanup issues */
      }
    }
    aiSelectionRef.current = null;
    aiCaretRef.current = null;
  }, []);

  const handleAiGenerate = useCallback(
    async (modeOverride, options = {}) => {
      const {
        forceUseSelection = null,
        forceScope = null,
        autoApplyResult = null,
        skipPreview = false,
      } = options || {};
      const editor = joditRef.current;
      if (!editor) return;

      if (aiBlocksRef.current.size === 0) {
        collectAiBlocks();
      }

      const preset = AI_PRESETS.find(item => item.id === (modeOverride || aiMode)) || AI_PRESETS[0];
      if (preset.id !== aiMode) {
        setAiMode(preset.id);
      }

      if (preset.requiresLanguage && !selectedLanguage) {
        setAiStatus({ state: 'error', message: 'Choose a language before generating.' });
        return;
      }

      const languages = Array.isArray(aiConfig.languages) ? aiConfig.languages : [];
      const languageEntry = languages.find(lang => lang.id === selectedLanguage);
      const languageLabel = languageEntry?.label || selectedLanguage || '';

      const initialSelectionHtml = editor.s.html || aiSelectionSummary.html || '';
      const initialSelectionText = htmlToPlainText(initialSelectionHtml) || aiSelectionSummary.text;
      const fullHtml = contentRef.current || editor.value || '';
      const blockEntry = aiSelectedBlockId ? aiBlocksRef.current.get(aiSelectedBlockId) : null;
      const blockNode = blockEntry ? resolveBlockNode(aiSelectedBlockId) : null;

      let selectionHtml = blockNode?.innerHTML || blockEntry?.html || initialSelectionHtml;
      let selectionText = htmlToPlainText(selectionHtml) || initialSelectionText;

      const useSelection =
        typeof forceUseSelection === 'boolean'
          ? forceUseSelection
          : aiRestrictToSelection || preset.scope === 'selection' || Boolean(blockEntry);

      if (useSelection && !selectionText.trim()) {
        setAiStatus({ state: 'error', message: 'Select some text or disable “Only work on selection”.' });
        setAiProgress(0);
        return;
      }

      if (!useSelection) {
        selectionHtml = fullHtml;
        selectionText = htmlToPlainText(fullHtml);
      }

      const effectiveScope = blockEntry ? 'selection' : useSelection ? 'selection' : preset.scope || 'selection';
      const resolvedScope = forceScope || effectiveScope;

      const selectionPayload =
        resolvedScope === 'full-email' ? selectionHtml : selectionText || '';

      setAiSelectionSummary(prev => ({
        ...prev,
        html: selectionHtml,
        text: selectionText || '',
        scope: resolvedScope,
        blockId: aiSelectedBlockId || null,
      }));

      const contextText = htmlToPlainText(editor.value || '');
      const snippetIds = selectedSnippets.filter(id => snippetLookup.has(id));
      const snippetPayload = snippetIds.map(id => ({ id }));

      setAiStatus({ state: 'loading', message: 'Generating copy…' });
      setAiPreview('');
      setAiProgress(12);
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
      }
      progressTimerRef.current = setInterval(() => {
        setAiProgress(prev => (prev >= 92 ? prev : prev + 4));
      }, 350);

      try {
        const headers = {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        };
        const response = await fetch(apiUrl('ai/generate'), {
          method: 'POST',
          headers,
          body: JSON.stringify({
            prompt: aiPrompt,
            command: preset.id,
            selection: selectionPayload || '',
            context: contextText,
            language: preset.requiresLanguage ? selectedLanguage : '',
            snippets: snippetPayload,
            scope: resolvedScope,
            targetSection:
              (blockEntry && blockEntry.label) || aiSelectionSummary.sectionId || '',
          }),
        });
        const retryAfterHeader = response.headers.get('retry-after');
        const retryAfterSeconds = Number.parseInt(retryAfterHeader || '', 10);
        let data = {};
        try {
          data = await response.json();
        } catch {
          data = {};
        }
        if (!response.ok) {
          if (response.status === 429) {
            if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
              throw new Error(
                `AI rate limit reached. Try again in about ${retryAfterSeconds} seconds.`
              );
            }
            throw new Error('AI rate limit reached. Please wait a moment and try again.');
          }
          throw new Error(data.error || 'AI request failed');
        }
        const result = (data.result || '').trim();
        if (!result) {
          throw new Error('The AI response was empty.');
        }
        const sanitised = sanitizeAiHtml(result, {
          allowStructure: preset.insertion === 'block' || resolvedScope !== 'selection',
          allowFullDocument: Boolean(autoApplyResult && resolvedScope === 'full-email'),
        });
        if (!sanitised) {
          throw new Error('The AI response did not contain usable text.');
        }

        const excerpt = htmlToPlainText(sanitised).replace(/\s+/g, ' ').trim();
        setAiHistory(prev => {
          const next = [
            {
              id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
              mode: preset.id,
              preset,
              languageId: preset.requiresLanguage ? selectedLanguage : '',
              languageLabel,
              snippets: snippetIds,
              preview: sanitised,
              excerpt,
              scope: resolvedScope,
              timestamp: Date.now(),
            },
            ...prev,
          ];
          return next.slice(0, 8);
        });

        if (typeof autoApplyResult === 'function') {
          setAiStatus({ state: 'success', message: 'Applying AI result…' });
          autoApplyResult(sanitised);
        } else {
          if (!skipPreview) {
            setAiPreview(sanitised);
          }
          setAiStatus({ state: 'success', message: 'Draft ready' });
        }
        setAiProgress(100);
        setTimeout(() => setAiProgress(0), 600);

        if (typeof autoApplyResult === 'function') {
          autoApplyResult(sanitised);
        } else {
          if (!skipPreview) {
            setAiPreview(sanitised);
          } else {
            setAiPreview('');
          }
          setAiStatus({ state: 'success', message: 'Draft ready' });
          setAiProgress(100);
          setTimeout(() => setAiProgress(0), 600);
        }
      } catch (error) {
        console.error('AI generate error', error);
        setAiStatus({ state: 'error', message: error.message || 'Failed to generate copy' });
        setAiProgress(0);
      } finally {
        if (progressTimerRef.current) {
          clearInterval(progressTimerRef.current);
          progressTimerRef.current = null;
        }
      }
    },
    [
      aiMode,
      aiPrompt,
      aiSelectionSummary.html,
      aiSelectionSummary.text,
      aiSelectionSummary.sectionId,
      aiConfig,
      selectedLanguage,
      selectedSnippets,
      snippetLookup,
      aiRestrictToSelection,
      aiSelectedBlockId,
      collectAiBlocks,
      resolveBlockNode,
    ]
  );

  const updateMatchedSetting = useCallback(
    (match, aiHtml) => {
      if (!match) return;
      const plain = htmlToPlainText(aiHtml || '').trim();
      if (!plain) return;

      if (match.type === 'field' && match.key) {
        onUpdate(match.key, plain);
        return;
      }

      if (match.type === 'sectionTitle' && Number.isInteger(match.index)) {
        const titles = Array.isArray(settings?.sectionTitles)
          ? settings.sectionTitles.map((title, idx) =>
              idx === match.index ? { ...(title || {}), text: plain } : title
            )
          : null;
        if (titles) {
          onUpdate('sectionTitles', titles);
        }
        return;
      }

      if (match.type === 'cta' && Number.isInteger(match.index)) {
        const buttons = Array.isArray(settings?.ctaButtons)
          ? settings.ctaButtons.map((button, idx) =>
              idx === match.index ? { ...(button || {}), text: plain } : button
            )
          : null;
        if (buttons) {
          onUpdate('ctaButtons', buttons);
        }
      }
    },
    [onUpdate, settings]
  );

  const handleApplyFullTranslation = useCallback(
    html => {
      const nextHtml = extractBody(html) || html || '';
      handleContentChange(nextHtml);
      showNotification('Translated entire email.', 'success');
      resetAiPanelState();
    },
    [handleContentChange, resetAiPanelState, showNotification]
  );

  const handleTranslateEntireEmail = useCallback(() => {
    const editor = joditRef.current;
    if (!editor) return;
    const translationPreset = AI_PRESETS.find(preset => preset.id === 'translate');
    if (translationPreset?.requiresLanguage && !selectedLanguage) {
      setAiStatus({ state: 'error', message: 'Select a language before translating the entire email.' });
      return;
    }
    const fullHtml = editor.value || '';
    if (!fullHtml.trim()) {
      setAiStatus({ state: 'error', message: 'There is no content to translate yet.' });
      return;
    }
    setAiRestrictToSelection(false);
    setAiSelectedBlockId('');
    applyBlockHighlight(null);
    const fullText = htmlToPlainText(fullHtml);
    setAiSelectionSummary(prev => ({
      ...prev,
      html: fullHtml,
      text: fullText,
      sectionId: null,
      fieldPath: null,
      scope: 'full-email',
      blockId: null,
      rangeInfo: null,
    }));
    handleAiGenerate('translate', {
      forceUseSelection: false,
      forceScope: 'full-email',
      autoApplyResult: handleApplyFullTranslation,
      skipPreview: true,
    });
  }, [
    applyBlockHighlight,
    handleAiGenerate,
    handleApplyFullTranslation,
    selectedLanguage,
  ]);

  const handleAiReplace = useCallback(() => {
    if (!aiPreview) return;
    if (activePreset?.insertion !== 'replace') return;
    const editor = joditRef.current;
    if (!editor) return;
    const structuredReplacement = buildStructuredReplacement(
      aiSelectionSummary.html,
      aiPreview
    );
    if (!structuredReplacement) return;
    const directReplacementHtml =
      prepareAiHtml(aiPreview) || structuredReplacement;

    const blockId = aiSelectionSummary.blockId;
    const blockNode = blockId ? resolveBlockNode(blockId) : null;

    const attemptedRangeReplace =
      aiSelectionSummary.rangeInfo &&
      applyRangeInfoToEditor(editor, aiSelectionSummary.rangeInfo);

    if (attemptedRangeReplace) {
      editor.focus();
      editor.s.remove();
      editor.s.insertHTML(directReplacementHtml);
    } else if (blockNode) {
      blockNode.innerHTML = structuredReplacement;
      applyBlockHighlight(blockNode);
    } else {
      restoreSelectionIfNeeded(editor);
      editor.focus();

      const selection = editor.s.sel;
      if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
        editor.s.remove();
      }
      editor.s.insertHTML(structuredReplacement);
    }

    handleContentChange(editor.value);
    if (activePreset?.id === 'translate' && aiSelectionSummary.scope === 'selection') {
      updateMatchedSetting(aiSelectionSummary.fieldPath, aiPreview);
    }
    resetAiPanelState();
  }, [
    aiPreview,
    aiSelectionSummary.html,
    aiSelectionSummary.blockId,
    aiSelectionSummary.rangeInfo,
    activePreset,
    aiSelectionSummary.fieldPath,
    aiSelectionSummary.scope,
    handleContentChange,
    resetAiPanelState,
    restoreSelectionIfNeeded,
    resolveBlockNode,
    applyBlockHighlight,
    updateMatchedSetting,
  ]);

  const handleAiInsert = useCallback(() => {
    if (!aiPreview) return;
    const editor = joditRef.current;
    if (!editor) return;
    const preset = activePreset || AI_PRESETS[0];

    const html = prepareAiHtml(aiPreview);
    if (!html) return;

    restoreSelectionIfNeeded(editor);
    editor.focus();

    if (preset.insertion === 'block') {
      const blockHtml = wrapAiBlockHtml(preset.id, html).trim();
      if (!blockHtml) return;
      editor.s.insertHTML(`${blockHtml}<p></p>`);
    } else {
      const hasSelection = Boolean((aiSelectionSummary.html || '').trim());
      if (hasSelection) {
        handleAiReplace();
        return;
      }
      editor.s.insertHTML(html);
    }

    handleContentChange(editor.value);
    if (activePreset?.id === 'translate' && aiSelectionSummary.scope === 'selection') {
      updateMatchedSetting(aiSelectionSummary.fieldPath, aiPreview);
    }
    resetAiPanelState();
  }, [
    aiPreview,
    aiSelectionSummary.html,
    activePreset,
    handleAiReplace,
    handleContentChange,
    resetAiPanelState,
    restoreSelectionIfNeeded,
    aiSelectionSummary.fieldPath,
    aiSelectionSummary.scope,
    updateMatchedSetting,
  ]);

  const handleSelectHistory = useCallback(item => {
    if (!item) return;
    if (item.mode) {
      setAiMode(item.mode);
    }
    setAiPreview(item.preview || '');
    if (item.languageId) {
      setSelectedLanguage(item.languageId);
    }
    setSelectedSnippets(Array.isArray(item.snippets) ? item.snippets : []);
    if (typeof item.scope === 'string') {
      setAiRestrictToSelection(item.scope === 'selection');
    }
    setAiStatus({ state: 'success', message: 'Loaded previous draft.' });
  }, []);

  const handleToggleFullscreen = useCallback(() => {
    const workspace = workspaceRef.current;
    if (!workspace) return;
    if (document.fullscreenElement) {
      const exitPromise = document.exitFullscreen?.();
      if (exitPromise && typeof exitPromise.then === 'function') {
        exitPromise.catch(() => setIsFullscreen(false));
      } else {
        setIsFullscreen(false);
      }
      return;
    }
    const request =
      workspace.requestFullscreen ||
      workspace.webkitRequestFullscreen ||
      workspace.mozRequestFullScreen ||
      workspace.msRequestFullscreen;
    if (request) {
      const promise = request.call(workspace);
      if (promise && typeof promise.then === 'function') {
        promise
          .then(() => setIsFullscreen(true))
          .catch(() => setIsFullscreen(false));
      } else {
        setIsFullscreen(true);
      }
    }
  }, []);

  const handleDeviceToggle = useCallback(
    value => {
      const key = ensureDeviceKey(value);
      if (typeof onPreviewDeviceChange === 'function') {
        onPreviewDeviceChange(key);
      }
    },
    [onPreviewDeviceChange]
  );

  const handleUndo = useCallback(() => {
    const editor = joditRef.current;
    if (!editor) return;
    try {
      editor.history?.undo?.();
      handleContentChange(editor.value);
    } catch (error) {
      console.warn('Undo failed in visual editor', error);
    }
  }, [handleContentChange]);

  const handleRedo = useCallback(() => {
    const editor = joditRef.current;
    if (!editor) return;
    try {
      editor.history?.redo?.();
      handleContentChange(editor.value);
    } catch (error) {
      console.warn('Redo failed in visual editor', error);
    }
  }, [handleContentChange]);

  const handleBlockDrop = useCallback(
    event => {
      const editor = joditRef.current;
      if (!editor) return;
      const originalEvent = event?.originalEvent ?? event;
      const dataTransfer = originalEvent?.dataTransfer;
      if (!dataTransfer) return;
      const blockId = dataTransfer.getData('text/x-kit-block');
      if (!blockId) return;
      event.preventDefault?.();
      event.stopPropagation?.();
      originalEvent.preventDefault?.();
      originalEvent.stopPropagation?.();
      originalEvent.stopImmediatePropagation?.();
      dataTransfer.dropEffect = 'copy';
      const block = blockLookup.get(blockId);
      if (!block) return;

      editor.focus();
      if (typeof editor.s.insertCursorAtPoint === 'function') {
        try {
          editor.s.insertCursorAtPoint(originalEvent.clientX ?? 0, originalEvent.clientY ?? 0);
        } catch {
          // ignore placement failures
        }
      }
      handleInsertBlock(block);
    },
    [blockLookup, handleInsertBlock]
  );

  const handleResetToFormLayout = useCallback(() => {
    if (!globalSettings) return;
    if (
      !window.confirm(
        'Resetting will discard the visual editor design and revert to the form-based layout. Continue?'
      )
    )
      return;

    const fallbackHtml = generateEmailHtml(settings, globalSettings, {
      ignoreVisualEditor: true,
    });
    const cleanHtml = stripLeadingStyleTag(extractBody(fallbackHtml));
    contentRef.current = cleanHtml;
    setContent(cleanHtml);
    setSaveStatus({ state: 'pending', savedAt: null });
    onUpdate('visualEditor', null);
    showNotification('Visual editor cleared. Using form-based configuration.', 'success');
  }, [globalSettings, settings, onUpdate, showNotification]);

  useEffect(() => {
    if (!editorReady) return;
    updateHistoryState();
    updateFormatState();
  }, [editorReady, updateHistoryState, updateFormatState]);

  useEffect(() => {
    if (!editorReady) return undefined;
    const editor = joditRef.current;
    if (!editor) return undefined;

    const root = editor.editor;
    if (root) {
      root.classList.add('kit-jodit-scope');
    }

    const dropHandler = event => handleBlockDrop(event);
    const dragOverHandler = event => {
      const originalEvent = event?.originalEvent ?? event;
      const dataTransfer = originalEvent?.dataTransfer;
      if (dataTransfer?.types?.includes('text/x-kit-block')) {
        originalEvent.preventDefault?.();
        originalEvent.stopPropagation?.();
        dataTransfer.dropEffect = 'copy';
        event.preventDefault?.();
        event.stopPropagation?.();
      }
    };

    editor.events.on('drop', dropHandler);
    editor.events.on('dragover', dragOverHandler);

    const removeBranding = () => {
      const container = editor.container;
      if (!container) return;
      const credit = container.querySelector('.jodit-status-bar__item a[href*="jodit"]');
      if (credit?.parentElement) {
        credit.parentElement.remove();
      }
    };

    removeBranding();
    editor.events.on('afterInit', removeBranding);

    const selectionHandler = () => {
      updateHistoryState();
      updateFormatState();
    };
    editor.events.on('mouseup', selectionHandler);
    editor.events.on('keyup', selectionHandler);
    editor.events.on('afterCommand', selectionHandler);

    return () => {
      editor.events.off('drop', dropHandler);
      editor.events.off('dragover', dragOverHandler);
      if (root) {
        root.classList.remove('kit-jodit-scope');
      }
      editor.events.off('afterInit', removeBranding);
      editor.events.off('mouseup', selectionHandler);
      editor.events.off('keyup', selectionHandler);
      editor.events.off('afterCommand', selectionHandler);
    };
  }, [editorReady, handleBlockDrop, updateHistoryState, updateFormatState]);

  useEffect(() => {
    if (!editorReady) return undefined;
    const editor = joditRef.current;
    if (!editor) return undefined;

    const handlePaste = event => {
      const originalEvent = event?.originalEvent || event;
      const clipboardData = originalEvent?.clipboardData || window.clipboardData;
      if (!clipboardData) return;
      const text = clipboardData.getData('text/plain');
      if (!text) return;
      originalEvent.preventDefault?.();
      event?.preventDefault?.();
      const styledHtml = buildDefaultParagraphHtmlFromText(text, {
        bodyFontSize: settings?.bodyFontSize,
        paragraphLineHeight: settings?.paragraphLineHeight,
        bodyTextColor: settings?.bodyTextColor,
        templateFontFamily: settings?.templateFontFamily,
      });
      if (styledHtml) {
        editor.s.insertHTML(styledHtml);
      }
    };

    editor.events.on('paste', handlePaste);
    return () => {
      editor.events.off('paste', handlePaste);
    };
  }, [
    editorReady,
    settings?.bodyFontSize,
    settings?.paragraphLineHeight,
    settings?.bodyTextColor,
    settings?.templateFontFamily,
  ]);

  useEffect(() => {
    if (!editorReady) return;
    const editor = joditRef.current;
    if (!editor) return;
    const root = editor.editor;
    if (!root) return;

    const buttons = Array.isArray(settings?.ctaButtons) ? settings.ctaButtons : [];
    const buttonMap = new Map(buttons.map(button => [button.id, button]));
    let anchors = Array.from(root.querySelectorAll('a[data-cta-id]'));
    let mutated = false;
    const existingIds = new Set(anchors.map(anchor => anchor.getAttribute('data-cta-id')));
    const missingDomButtons = buttons.filter(
      button => button?.id && !existingIds.has(button.id)
    );
    if (missingDomButtons.length > 0) {
      const doc = editor.editorDocument || document;
      missingDomButtons.forEach(button => {
        const wrapper = doc.createElement('div');
        wrapper.innerHTML = createCtaBlockHtml(button, {
          fontFamily: fontStack,
          fontSizePt,
          lineHeightValue,
          textColor: settings?.bodyTextColor || '#1f2937',
        }).trim();
        const fragment = doc.createDocumentFragment();
        while (wrapper.firstChild) {
          fragment.appendChild(wrapper.firstChild);
        }
        root.appendChild(fragment);
        existingIds.add(button.id);
        mutated = true;
      });
      anchors = Array.from(root.querySelectorAll('a[data-cta-id]'));
    }

    anchors.forEach(anchor => {
      const ctaId = anchor.getAttribute('data-cta-id');
      if (!ctaId) return;
      const button = buttonMap.get(ctaId);
      if (!button) {
        const container = anchor.closest('[data-cta-block="true"]') || anchor.closest('table');
        if (container) {
          container.remove();
        } else {
          anchor.remove();
        }
        mutated = true;
        return;
      }

      const desiredHref = button.url || '#';
      if (anchor.getAttribute('href') !== desiredHref) {
        anchor.setAttribute('href', desiredHref);
        mutated = true;
      }

      const anchorStyle = anchor.style;
      const desiredBackground = button.backgroundColor || '#2563eb';
      const desiredColor = button.textColor || '#ffffff';
      const desiredPadding = `${Number(button.paddingY ?? 12)}px ${Number(button.paddingX ?? 24)}px`;
      const desiredRadius = `${Number(button.borderRadius ?? 999)}px`;
      const desiredFontSize = `${Number(button.fontSize ?? 16)}pt`;

      if (anchorStyle.background !== desiredBackground) {
        anchorStyle.background = desiredBackground;
        mutated = true;
      }
      if (anchorStyle.color !== desiredColor) {
        anchorStyle.color = desiredColor;
        mutated = true;
      }
      if (anchorStyle.padding !== desiredPadding) {
        anchorStyle.padding = desiredPadding;
        mutated = true;
      }
      if (anchorStyle.borderRadius !== desiredRadius) {
        anchorStyle.borderRadius = desiredRadius;
        mutated = true;
      }
      if (anchorStyle.fontSize !== desiredFontSize) {
        anchorStyle.fontSize = desiredFontSize;
        mutated = true;
      }
      if (anchorStyle.fontWeight !== '600') {
        anchorStyle.fontWeight = '600';
        mutated = true;
      }
      if (anchorStyle.textDecoration !== 'none') {
        anchorStyle.textDecoration = 'none';
        mutated = true;
      }
      if (anchorStyle.display !== 'inline-block') {
        anchorStyle.display = 'inline-block';
        mutated = true;
      }

      const displayText = button.text || '';
      if (anchor.textContent !== displayText) {
        anchor.textContent = displayText;
        mutated = true;
      }

      const container = anchor.closest('[data-cta-block="true"]');
      const containerCell = container?.querySelector('td');
      if (containerCell) {
        if (containerCell.style.fontFamily !== fontStack) {
          containerCell.style.fontFamily = fontStack;
          mutated = true;
        }
        if (containerCell.style.fontSize !== fontSizePt) {
          containerCell.style.fontSize = fontSizePt;
          mutated = true;
        }
        if (containerCell.style.lineHeight !== lineHeightValue) {
          containerCell.style.lineHeight = lineHeightValue;
          mutated = true;
        }
        const desiredTextColor = settings?.bodyTextColor || '#1f2937';
        if (containerCell.style.color !== desiredTextColor) {
          containerCell.style.color = desiredTextColor;
          mutated = true;
        }
      }
    });

    if (mutated) {
      handleContentChange(editor.value);
    }
  }, [
    editorReady,
    settings?.ctaButtons,
    settings?.bodyTextColor,
    fontStack,
    fontSizePt,
    lineHeightValue,
    handleContentChange,
  ]);

  const saveStatusLabel = useMemo(() => {
    switch (saveStatus.state) {
      case 'pending':
        return 'Pending changes…';
      case 'saving':
        return 'Saving…';
      case 'saved':
        return saveStatus.savedAt
          ? `Saved ${formatRelativeTime(saveStatus.savedAt, relativeNow)}`
          : 'Saved';
      default:
        return 'No changes';
    }
  }, [saveStatus.state, saveStatus.savedAt, relativeNow]);

  const statusToneClass = useMemo(() => {
    switch (saveStatus.state) {
      case 'pending':
        return 'bg-amber-50 text-amber-700 border border-amber-200';
      case 'saving':
        return 'bg-blue-50 text-blue-600 border border-blue-200';
      case 'saved':
        return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
      default:
        return 'bg-slate-100 text-slate-500 border border-slate-200';
    }
  }, [saveStatus.state]);

  const joditConfig = useMemo(
    () => ({
      readonly: false,
      toolbar: false,
      toolbarAdaptive: false,
      statusbar: true,
      showCharsCounter: true,
      showWordsCounter: true,
      showXPathInStatusbar: true,
      spellcheck: false,
      speechRecognize: false,
      speechRecognizeUrl: '',
      askBeforePasteHTML: false,
      askBeforePasteFromWord: false,
      tabIndex: -1,
      enter: 'P',
      dragAndDrop: true,
      useSearch: true,
      autofocus: false,
      allowResizeX: false,
      allowResizeY: false,
      showPlaceholder: true,
      placeholder: 'Start designing your email…',
      removeButtons: ['speechRecognize', 'spellcheck', 'ai-assistant', 'classSpan', 'className'],
      disablePlugins: ['about', 'ai-assistant', 'speechRecognition'],
    }),
    []
  );

  const handleEditorRef = useCallback(instance => {
    if (instance) {
      joditRef.current = instance;
      setEditorReady(true);
    } else {
      joditRef.current = null;
      setEditorReady(false);
    }
  }, []);

  useEffect(() => {
    const workspaceNode = workspaceRef.current;
    return () => {
      // Let jodit-react own editor lifecycle; only clear local ref/UI state here.
      joditRef.current = null;

      if (workspaceNode) {
        workspaceNode.classList.remove('kit-fullscreen-active');
      }

      if (typeof document !== 'undefined' && document.fullscreenElement) {
        document.exitFullscreen?.().catch(() => {});
      }
    };
  }, []);

  if (!globalSettings) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 p-4 rounded-lg">
        <p className="font-semibold">Loading global assets…</p>
        <p className="text-sm">Global settings are required before the visual editor can initialise.</p>
      </div>
    );
  }

  const activeLinkCopy = LINK_TYPE_COPY[linkToolState.mode] || LINK_TYPE_COPY.web;

  return (
    <div ref={workspaceRef} className={WORKSPACE_CLASS}>
      <div className="glass-panel px-4 py-3 space-y-3 text-slate-700">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleToggleFullscreen}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary/40"
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              <span className="h-3.5 w-3.5 text-slate-500" aria-hidden="true">
                {isFullscreen ? <IconMinimise /> : <IconMaximise />}
              </span>
              {isFullscreen ? 'Minimise' : 'Fullscreen'}
            </button>
            <button
              type="button"
              onClick={handleResetToFormLayout}
              className="inline-flex items-center rounded-lg border border-rose-200 bg-white px-3 py-1.5 text-xs font-semibold text-rose-600 transition hover:border-rose-300"
            >
              Reset to form
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 font-semibold ${statusToneClass}`}>
              {saveStatusLabel}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleUndo}
                disabled={!historyState.canUndo}
                className={`inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-semibold border transition ${
                  historyState.canUndo
                    ? 'border-slate-300 bg-white text-slate-700 hover:border-primary/40'
                    : 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                }`}
              >
                Undo
              </button>
              <button
                type="button"
                onClick={handleRedo}
                disabled={!historyState.canRedo}
                className={`inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-semibold border transition ${
                  historyState.canRedo
                    ? 'border-slate-300 bg-white text-slate-700 hover:border-primary/40'
                    : 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                }`}
              >
                Redo
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="kit-editor-panels">
        <aside className="glass-panel flex flex-col overflow-hidden">
          <div className="px-3 pt-3">
            <h3 className="text-sm font-semibold text-slate-800">Blocks</h3>
            <p className="mt-1 text-xs text-slate-500">Drag or click a block to insert it into the canvas.</p>
          </div>
          <div className="relative mt-3 flex-1 overflow-hidden rounded-2xl border border-slate-200/70 bg-white/70">
            <div className="absolute inset-0 overflow-y-auto px-2 py-3">
              <div className="space-y-3">
                {blockLibrary.map(block => (
                  <div
                    key={block.id}
                    draggable
                    onDragStart={event => {
                      event.dataTransfer.setData('text/x-kit-block', block.id);
                      event.dataTransfer.effectAllowed = 'copy';
                    }}
                    onClick={() => handleInsertBlock(block)}
                    className="kit-block-card"
                  >
                    <h4>{block.label}</h4>
                    <p>{block.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </aside>

        <div className="glass-panel flex flex-col overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200/70 bg-white/80 px-4 py-2 text-xs text-slate-500">
            <div className="flex items-center gap-3">
              <div className="kit-view-toggle" role="tablist" aria-label="Editor view">
                <button
                  type="button"
                  role="tab"
                  aria-selected={viewMode === 'edit'}
                  onClick={() => setViewMode('edit')}
                  className={viewMode === 'edit' ? 'kit-view-toggle__active' : undefined}
                >
                  Edit
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={viewMode === 'preview'}
                  onClick={() => setViewMode('preview')}
                  className={viewMode === 'preview' ? 'kit-view-toggle__active' : undefined}
                >
                  Preview
                </button>
              </div>
              <ResponsiveDeviceToggle
                value={normalizedDevice}
                onChange={handleDeviceToggle}
                variant="compact"
              />
            </div>
          </div>
          <div className="flex-1 overflow-auto">
            <div
              className="mx-auto"
              style={{
                maxWidth: `${previewContainerWidth}px`,
              }}
            >
              <div className="kit-rich-editor">
                <div
                  className="kit-custom-toolbar-container"
                  ref={toolbarRef}
                  style={{ display: viewMode === 'edit' ? 'block' : 'none' }}
                >
                  <div className="kit-custom-toolbar">
                    <div className="kit-toolbar-group">
                      <div className="kit-fontsize-control">
                        <label htmlFor={FONT_SIZE_INPUT_ID}>Size</label>
                        <input
                          id={FONT_SIZE_INPUT_ID}
                          list={FONT_SIZE_OPTIONS_ID}
                          type="number"
                          min={8}
                          max={96}
                          step={1}
                          inputMode="numeric"
                          value={fontSizeDraft}
                          onChange={handleFontSizeInputChange}
                          onBlur={commitFontSizeDraft}
                          onKeyDown={event => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              commitFontSizeDraft();
                            }
                          }}
                          placeholder="Auto"
                        />
                        <datalist id={FONT_SIZE_OPTIONS_ID}>
                          {FONT_SIZE_PRESETS.map(size => (
                            <option key={size} value={size} />
                          ))}
                        </datalist>
                      </div>

                      <button
                        type="button"
                        className="kit-toolbar-button kit-toolbar-button--compact"
                        onClick={() => handleFontSizeStep(-1)}
                        title="Decrease font size"
                      >
                        -
                      </button>
                      <button
                        type="button"
                        className="kit-toolbar-button kit-toolbar-button--compact"
                        onClick={() => handleFontSizeStep(1)}
                        title="Increase font size"
                      >
                        +
                      </button>
                      <button
                        type="button"
                        className="kit-toolbar-button kit-toolbar-button--compact"
                        onClick={handleFontSizeReset}
                        title="Reset to document default"
                      >
                        Reset
                      </button>
                    </div>

                    <span className="kit-toolbar-divider" />

                    <div className="kit-toolbar-group">
                      <button
                        type="button"
                        onClick={() => runEditorCommand('bold')}
                        className={`kit-toolbar-button ${formatState.bold ? 'kit-toolbar-button--active' : ''}`}
                        aria-pressed={formatState.bold}
                        aria-label="Bold"
                        title="Bold"
                      >
                        <span className="kit-toolbar-button__glyph" aria-hidden="true">
                          B
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => runEditorCommand('italic')}
                        className={`kit-toolbar-button ${formatState.italic ? 'kit-toolbar-button--active' : ''}`}
                        aria-pressed={formatState.italic}
                        aria-label="Italic"
                        title="Italic"
                      >
                        <span className="kit-toolbar-button__glyph" aria-hidden="true">
                          I
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => runEditorCommand('underline')}
                        className={`kit-toolbar-button ${formatState.underline ? 'kit-toolbar-button--active' : ''}`}
                        aria-pressed={formatState.underline}
                        aria-label="Underline"
                        title="Underline"
                      >
                        <span className="kit-toolbar-button__glyph" aria-hidden="true">
                          U
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => runEditorCommand('strikeThrough')}
                        className={`kit-toolbar-button ${formatState.strike ? 'kit-toolbar-button--active' : ''}`}
                        aria-pressed={formatState.strike}
                        aria-label="Strikethrough"
                        title="Strikethrough"
                      >
                        <span className="kit-toolbar-button__glyph" aria-hidden="true">
                          S
                        </span>
                      </button>
                    </div>

                    <span className="kit-toolbar-divider" />

                    <div className="kit-toolbar-group">
                      <button
                        type="button"
                        onClick={() => handleAlignChange('left')}
                        className={`kit-toolbar-button ${formatState.alignment === 'left' ? 'kit-toolbar-button--active' : ''}`}
                        aria-pressed={formatState.alignment === 'left'}
                        aria-label="Align left"
                        title="Align left"
                      >
                        <span className="kit-toolbar-button__icon" aria-hidden="true">
                          <IconAlignLeft />
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAlignChange('center')}
                        className={`kit-toolbar-button ${formatState.alignment === 'center' ? 'kit-toolbar-button--active' : ''}`}
                        aria-pressed={formatState.alignment === 'center'}
                        aria-label="Align centre"
                        title="Align centre"
                      >
                        <span className="kit-toolbar-button__icon" aria-hidden="true">
                          <IconAlignCenter />
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAlignChange('right')}
                        className={`kit-toolbar-button ${formatState.alignment === 'right' ? 'kit-toolbar-button--active' : ''}`}
                        aria-pressed={formatState.alignment === 'right'}
                        aria-label="Align right"
                        title="Align right"
                      >
                        <span className="kit-toolbar-button__icon" aria-hidden="true">
                          <IconAlignRight />
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAlignChange('justify')}
                        className={`kit-toolbar-button ${formatState.alignment === 'justify' ? 'kit-toolbar-button--active' : ''}`}
                        aria-pressed={formatState.alignment === 'justify'}
                        aria-label="Justify"
                        title="Justify"
                      >
                        <span className="kit-toolbar-button__icon" aria-hidden="true">
                          <IconAlignJustify />
                        </span>
                      </button>
                    </div>

                    <span className="kit-toolbar-divider" />

                    <div className="kit-toolbar-group">
                      <button
                        type="button"
                        onClick={() => handleToggleList('unordered')}
                        className={`kit-toolbar-button ${formatState.unorderedList ? 'kit-toolbar-button--active' : ''}`}
                        aria-pressed={formatState.unorderedList}
                        aria-label="Bulleted list"
                        title="Bulleted list"
                      >
                        <span className="kit-toolbar-button__icon" aria-hidden="true">
                          <IconListBulleted />
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleToggleList('ordered')}
                        className={`kit-toolbar-button ${formatState.orderedList ? 'kit-toolbar-button--active' : ''}`}
                        aria-pressed={formatState.orderedList}
                        aria-label="Numbered list"
                        title="Numbered list"
                      >
                        <span className="kit-toolbar-button__icon" aria-hidden="true">
                          <IconListNumbered />
                        </span>
                      </button>
                    </div>

                    <span className="kit-toolbar-divider" />

                    <div className="kit-toolbar-group">
                      <button
                        type="button"
                        ref={linkButtonRef}
                        onClick={openLinkTool}
                        className={`kit-toolbar-button ${
                          formatState.linkActive || linkToolState.isOpen ? 'kit-toolbar-button--active' : ''
                        }`}
                        aria-expanded={linkToolState.isOpen}
                        aria-pressed={formatState.linkActive}
                        aria-label="Insert link"
                        title="Insert link"
                      >
                        <span className="kit-toolbar-button__icon" aria-hidden="true">
                          <IconChain />
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={handleRemoveLink}
                        disabled={!formatState.linkActive}
                        className={`kit-toolbar-button ${
                          formatState.linkActive ? 'kit-toolbar-button--active' : 'kit-toolbar-button--disabled'
                        }`}
                        aria-pressed={formatState.linkActive}
                        aria-label="Remove link"
                        title="Remove link"
                      >
                        <span className="kit-toolbar-button__icon" aria-hidden="true">
                          <IconUnlink />
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={handleClearFormatting}
                        className="kit-toolbar-button"
                        aria-label="Clear formatting"
                        title="Clear formatting"
                      >
                        <span className="kit-toolbar-button__icon" aria-hidden="true">
                          <IconEraser />
                        </span>
                      </button>
                    </div>
                  </div>

                  {linkToolState.isOpen && (
                    <div
                      ref={linkPopoverRef}
                      className="kit-link-popover"
                      style={{
                        top: `${Math.max(linkPopoverCoords.top, 0)}px`,
                        left: `${Math.max(linkPopoverCoords.left, 0)}px`,
                      }}
                    >
                      <div className="kit-link-type-switch">
                        {LINK_TYPES.map(type => (
                          <button
                            key={type.id}
                            type="button"
                            className={
                              linkToolState.mode === type.id ? 'kit-link-type-switch__active' : undefined
                            }
                            onClick={() => {
                              setLinkToolState(prev => ({
                                ...prev,
                                mode: type.id,
                              }));
                              setLinkToolError('');
                            }}
                          >
                            {type.label}
                          </button>
                        ))}
                      </div>
                      <div>
                        <label htmlFor="kit-link-text">Display text</label>
                        <input
                          id="kit-link-text"
                          type="text"
                          value={linkToolState.displayText}
                          onChange={event => handleLinkFieldChange('displayText', event.target.value)}
                          placeholder="Link text"
                        />
                      </div>
                      <div>
                        <label htmlFor="kit-link-target">{activeLinkCopy.addressLabel}</label>
                        <input
                          id="kit-link-target"
                          type="text"
                          value={linkToolState.address}
                          onChange={event => handleLinkFieldChange('address', event.target.value)}
                          placeholder={activeLinkCopy.placeholder}
                          onKeyDown={event => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              applyLink();
                            }
                          }}
                        />
                      </div>
                      {activeLinkCopy.helper && (
                        <div className="kit-link-popover__helper">{activeLinkCopy.helper}</div>
                      )}
                      {linkToolError ? (
                        <div className="kit-link-popover__error">{linkToolError}</div>
                      ) : null}
                      <div className="kit-link-popover__actions">
                        <button type="button" onClick={closeLinkTool}>
                          Cancel
                        </button>
                        <button type="button" className="kit-primary" onClick={applyLink}>
                          Apply link
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ display: viewMode === 'edit' ? 'block' : 'none' }}>
                  <JoditEditor
                    ref={handleEditorRef}
                    value={editorValue}
                    config={joditConfig}
                    onChange={handleContentChange}
                  />
                </div>

                <div
                  className={`kit-preview-canvas${
                    isMobilePreviewDevice ? ' kit-preview-canvas--mobile' : ''
                  }`}
                  style={{ display: viewMode === 'preview' ? 'block' : 'none' }}
                >
                  <iframe
                    title="Email preview"
                    srcDoc={previewDocument}
                    style={{
                      width: '100%',
                      minHeight: `${Math.max(previewPreset.height || 0, 620)}px`,
                      border: '0',
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {VISUAL_EDITOR_AI_ENABLED ? (
        <div className="fixed bottom-6 right-6 z-[115]">
          <button
            type="button"
            onClick={handleOpenAiPanel}
            className="inline-flex items-center gap-2 rounded-full border border-primary bg-primary px-4 py-2 text-xs font-semibold text-white shadow-lg transition hover:shadow-xl"
            aria-haspopup="dialog"
          >
            AI Assist
          </button>
        </div>
      ) : null}

      {VISUAL_EDITOR_AI_ENABLED ? (
        <AiAssistantPanel
          open={isAiPanelOpen}
          mode={aiMode}
          presets={AI_PRESETS}
          activePreset={activePreset}
          onModeChange={setAiMode}
          selectionText={aiSelectionSummary.text}
          promptValue={aiPrompt}
          onPromptChange={setAiPrompt}
          onGenerate={handleAiGenerate}
          onInsert={handleAiInsert}
          onReplace={handleAiReplace}
          onClose={handleCloseAiPanel}
          status={aiStatus}
          preview={aiPreview}
          isGenerating={aiStatus.state === 'loading'}
          onUseSelection={handleAiCaptureSelection}
          canReplace={
            activePreset?.insertion === 'replace' &&
            Boolean((aiSelectionSummary.html || '').trim())
          }
          languages={aiConfig.languages}
          selectedLanguage={selectedLanguage}
          onLanguageChange={setSelectedLanguage}
          snippetCategories={aiConfig.snippetCategories}
          selectedSnippets={selectedSnippets}
          onToggleSnippet={handleToggleSnippet}
          progress={aiProgress}
          history={aiHistory}
          onSelectHistory={handleSelectHistory}
          restrictToSelection={aiRestrictToSelection}
          onToggleRestrict={handleRestrictToggle}
          blocks={aiBlockOptions}
          selectedBlockId={aiSelectedBlockId}
          onSelectBlock={handleAiSelectBlock}
          onTranslateFullEmail={handleTranslateEntireEmail}
        />
      ) : null}
    </div>
  );
};

export default VisualEditorSection;
